/* AstroSong UGC In-Browser Recorder — v3 (record-on-reveal-page refactor)
 *                                       + iOS WebCodecs capture engine (2026-06)
 *
 * Resolves the "1-phone problem" (feedback_ugc_one_phone_problem).
 *
 * v3 ARCHITECTURE CHANGE (2026-05-21, validated by Lucas):
 *   The DURING-RECORDING experience no longer lives in a duplicated full-screen
 *   overlay (that double-rendered the layout — second particle system + second
 *   bg over the live reveal page = A53 lag). Instead we record ON the real
 *   reveal page: at countdown end we CLOSE the overlay, start MediaRecorder,
 *   and show a light "recording mode" layer (#asRecordingLayer) over the real
 *   vinyl / lyrics / stars / controls — just 4 cues: red frame, REC pill,
 *   face PiP, STOP RECORDING. Upsell cards are hidden so she can't click away.
 *   ONE render path. The reveal page's own particles/vinyl carry the visuals.
 *
 * ENGINE SWAP (2026-06, iOS-ONLY, under-the-hood — ZERO UX/UI change):
 *   iOS Safari's MediaRecorder VIDEO encoder stalls ~14s (unfixable WebKit bug)
 *   so on iOS we capture VIDEO with WebCodecs (proven end-to-end on a real
 *   iPhone, iOS 18.7) instead. The recorder talks to a generic captureEngine
 *   interface { kind, mimeExt, prewarm, start, pause, resume, stop->Blob,
 *   discard, hardRelease } with TWO implementations:
 *     • MediaRecorderEngine — the CURRENT record logic, relocated byte-behavior-
 *       identically. Used on ALL Android, ALL desktop, and old iOS lacking
 *       WebCodecs/OPFS. Zero regression on the working paths.
 *     • WebCodecsEngine — getUserMedia stream -> off-DOM <video> -> rVFC ->
 *       canvas (9:16 center-crop, non-mirrored) -> VideoFrame -> VideoEncoder
 *       (H.264 High, realtime, ~8Mbps) muxed by Mediabunny into a fragmented
 *       MP4 streamed memory-safe to an OPFS dedicated-Worker sync handle; audio
 *       via an audio-only MediaRecorder (iOS audio path has no bug) demuxed +
 *       muxed as an AAC/Opus track. iOS-GATED: NEVER constructed off-iOS (its
 *       canvas-encode load can never touch a weak Android). Pre-warmed during
 *       the EXISTING 3-2-1 countdown so recording starts with NO added delay.
 *   The visitor's screens, copy, mirror, REC chrome, preview, Send/Discard,
 *   savePending, salvage, and songStartMs are all UNCHANGED — the only
 *   observable difference is a clean full-length iOS MP4 instead of a 14s stall.
 *
 * Privacy (Lucas's #1 rule): camera + mic MUST be released the instant the
 * user leaves the page, hides the tab, navigates away, OR closes the recorder.
 * Multiple redundant cleanup triggers (visibilitychange, pagehide,
 * beforeunload, close, discard). Losing the camera light = legal/PR
 * catastrophe per Lucas. WebCodecs adds an OPFS scratch MP4 that is DELETED on
 * discard / send / abandon / teardown (the read-back Blob is already in RAM).
 *
 * UX flow: popup (offer -> permission) -> pre-record framing (overlay) ->
 * countdown 3-2-1 (overlay) -> RECORD ON REAL PAGE (intro pre-song -> tap
 * REVEAL MY ASTROSONG -> song plays on the real page -> STOP RECORDING) ->
 * preview (overlay, custom scrubber) -> Send / Discard. NO retry.
 */
(function () {
  var overlay = document.getElementById('asRecorderOverlay');
  if (!overlay) return;

  var revealRoot = document.getElementById('astroSongReveal');
  var songAudio = document.getElementById('audio');
  var firstPlayOverlay = document.getElementById('firstPlayOverlay');
  var recLayer = document.getElementById('asRecordingLayer');

  var state = {
    open: false,
    view: null,
    viewBeforeExit: null,
    mediaStream: null,
    mediaRecorder: null,
    mimeType: null,
    recordedChunks: [],
    recordedBlob: null,
    recordedUrl: null,
    songStarted: false,
    songStartMs: null, /* offset (ms) of song start within the recording; null = unknown (legacy/salvaged) */
    startedAt: null,
    elapsedTimerId: null,
    pausedAccumMs: 0,
    pausedAt: null,
    warned: false,
    autoStopped: false,
    /* True while we're finalizing a recording after an interruption (incoming
       call / app switch / unload). Stops the destructive hardClose path from
       nuking the chunks before onstop persists them. */
    salvaging: false,
    /* 9:16 canvas capture */
    canvas: null,
    srcVideo: null,
    drawRAF: null,
    canvasStream: null,
    recordStream: null,
    /* Active capture engine (MediaRecorderEngine | WebCodecsEngine) — the single
       seam the recorder drives instead of touching MediaRecorder directly. */
    engine: null,
    /* Engine-agnostic "a take is live" flag — replaces every old
       `state.mediaRecorder && state.mediaRecorder.state==='recording'||'paused'`
       liveness probe. Set true after engine.start(), false in
       finalizeStop/onRecordingFinished. */
    recordingActive: false,
    /* True from the moment finalizeStop() begins the (async, multi-second on
       WebCodecs) engine.stop() until onRecordingFinished resolves it. While true,
       onInterrupt must NOT hardClose/delete-OPFS — that would destroy the take
       mid-finalize and re-open the overlay with an empty blob. */
    finalizing: false,
    /* One-shot latch: onRecordingFinished may run AT MOST once per take, and never
       after a hardClose has torn the engine down (prevents an empty-blob save +
       contradictory view re-open if a late stop() resolves post-teardown). */
    finished: false,
  };

  /* True only while a TRUE page-leave (reload / back / close) is in progress — set on
     beforeunload / non-bfcache pagehide, reset on pageshow. Lets onRecordingFinished
     skip the (flashing) interruption screen on a dying page; the partial is recovered
     cleanly on the NEXT load instead. NOT set on app-switch/lock (page stays alive →
     the in-session 'interrupted' view is correct there). */
  var unloading = false;

  /* Long-video safety: warn ~1 min before the cap, hard auto-stop at the cap so
     she's never left with an unsendable 10-min file. (Counts recording time
     only — paused time is excluded via pausedAccumMs.) */
  var REC_WARN_SEC = 7 * 60;  /* 7:00 — soft warning */
  var REC_MAX_SEC = 8 * 60;   /* 8:00 — hard auto-stop */

  /* 9:16 capture canvas. Lower fps = less encode load on weak phones (A53);
     24fps is plenty for a reaction video. Resolution is the next knob if the
     A53 still janks during the song — drop to 540×960 (quality vs perf). */
  var CANVAS_W = 720, CANVAS_H = 1280;
  var CAP_FPS = 24;
  var FRAME_MS = 1000 / CAP_FPS; /* ~41.7ms draw throttle */

  /* iOS is powerful + records clean mp4/h264 at full res. Cheap Android (A53)
     needs its capture resolution capped or the encoder datamoshes. */
  var IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '')
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  /* Capture THIS script's src NOW, synchronously (document.currentScript is
     valid here but null in later async callbacks). The WebCodecsEngine derives
     the same-origin mediabunny.min.js URL from it. */
  var ASSET_BASE = '';
  try {
    try { var __s0 = document.currentScript; if (__s0 && __s0.src) ASSET_BASE = __s0.src; } catch (e) {}
    if (!ASSET_BASE) {
      try {
        var __els0 = document.querySelectorAll('script[src]');
        for (var __i0 = __els0.length - 1; __i0 >= 0; __i0--) {
          if (/as-ugc-recorder\.js/.test(__els0[__i0].src || '')) { ASSET_BASE = __els0[__i0].src; break; }
        }
      } catch (e) {}
    }
  } catch (e) {}

  /* Screen Wake Lock — keep the screen ON during a recording so the phone
     doesn't auto-lock mid-reaction (a 4-6 min song with no touch → the screen
     would sleep → visibilitychange→hidden → her take ends). Acquired on the
     Start gesture; the browser auto-releases it when the page hides, and we also
     release it on every teardown path. Non-fatal where unsupported. */
  var wakeLock = null;
  var wakeLockWanted = false;
  function requestWakeLock() {
    wakeLockWanted = true;
    try {
      if (navigator.wakeLock && navigator.wakeLock.request) {
        navigator.wakeLock.request('screen').then(function (wl) {
          /* If the take ended before this resolved, release immediately — else the
             screen would stay awake until the page hides (a dangling lock). */
          if (!wakeLockWanted) { try { wl.release(); } catch (e) {} return; }
          wakeLock = wl;
        }).catch(function () {});
      }
    } catch (e) {}
  }
  function releaseWakeLock() {
    wakeLockWanted = false;
    if (wakeLock) { try { wakeLock.release(); } catch (e) {} wakeLock = null; }
  }

  /* ---------- Orphan-partial recovery marker (BUG1: hard-unload mid-finalize) ----------
   * The WebCodecs OPFS scratch accumulates a VALID fragmented MP4 while recording.
   * If the page is HARD-killed (reload / back-gesture / OS memory-kill) BEFORE the
   * async stop() chain persists the blob to IndexedDB, that partial is orphaned and
   * the next session's OPFS init truncates it. We set a localStorage marker at take
   * start (webcodecs only) and CLEAR it on every terminal path (blob safely in IDB /
   * discarded / sent / torn down). The marker's PRESENCE at next load = "a take died
   * in-flight, no IDB blob, maybe an OPFS partial" → tryRecoverOrphanPartial() reads
   * it back (VIDEO-ONLY: audio lived in the killed audio-only MediaRecorder = lost).
   * Because the marker is cleared the instant the IDB blob exists, it can never
   * double-fire with the card's own IndexedDB recovery (mutually exclusive). */
  var WC_OPFS_FILENAME = 'asugc-wc.mp4';
  function partialMarkerKey() { return 'asUgcPartial:' + ((revealRoot && revealRoot.dataset.handle) || ''); }
  function setPartialMarker() { try { localStorage.setItem(partialMarkerKey(), '1'); } catch (e) {} }
  function clearPartialMarker() { try { localStorage.removeItem(partialMarkerKey()); } catch (e) {} }

  function $(sel) { return overlay.querySelector(sel); }
  function $$(sel) { return overlay.querySelectorAll(sel); }
  function $L(sel) { return recLayer ? recLayer.querySelector(sel) : null; }

  function gtm(event, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: event };
      if (revealRoot) {
        payload.page_handle = revealRoot.dataset.handle || '';
        payload.order_number = revealRoot.dataset.orderNumber || '';
      }
      if (params) for (var k in params) payload[k] = params[k];
      window.dataLayer.push(payload);
    } catch (e) {}
  }

  function setView(view) {
    state.view = view;
    var views = $$('[data-recorder-view]');
    for (var i = 0; i < views.length; i++) {
      var v = views[i];
      if (v.dataset.recorderView === view) v.setAttribute('data-active', 'true');
      else v.removeAttribute('data-active');
    }
    /* The × is hidden on views that have their own actions / must not be exited
       by an accidental tap (uploading, success, the confirm modals, error). */
    var closeBtn = overlay.querySelector('[data-recorder-close]');
    if (closeBtn) {
      var hideClose = (view === 'uploading' || view === 'success' || view === 'send_error'
        || view === 'keep_confirm' || view === 'exit_confirm' || view === 'rerecord_confirm');
      closeBtn.style.display = hideClose ? 'none' : '';
    }
  }

  function pickMimeType() {
    var candidates = [
      'video/mp4;codecs=h264,aac',
      'video/mp4',
      'video/webm;codecs=vp8,opus', /* VP8 first — lighter + more stable on cheap Android HW/SW encoders (VP9 datamoshed on the A53) */
      'video/webm;codecs=vp9,opus',
      'video/webm',
    ];
    for (var i = 0; i < candidates.length; i++) {
      try {
        if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(candidates[i])) {
          return candidates[i];
        }
      } catch (e) {}
    }
    return null;
  }
  function isRecorderSupported() {
    return !!(window.MediaRecorder
      && navigator.mediaDevices
      && navigator.mediaDevices.getUserMedia
      && pickMimeType());
  }

  function open() {
    if (state.open) return;
    if (!isRecorderSupported()) {
      gtm('ugc_recorder_unsupported');
      alert("Your browser doesn't support in-browser recording. Use the upload option to send a video file instead.");
      return;
    }
    gtm('ugc_recorder_opened');
    overlay.setAttribute('data-open', 'true');
    document.body.classList.add('as-recorder-active');
    state.open = true;
    if (firstPlayOverlay) firstPlayOverlay.style.display = 'none';
    /* The reassurance step now lives in the popup (step 2), so opening the
       recorder goes straight to the permission request → pre_record. */
    requestPermissionAndShowPreRecord();
  }

  /* ---------- Popup step machine (step 1 offer → step 2 permission → retain) ---------- */
  var currentPopupStep = '1';
  var popupStepBeforeRetain = '1';

  function showPopupStep(step) {
    var steps = document.querySelectorAll('.as-fpStep');
    for (var i = 0; i < steps.length; i++) {
      var s = steps[i];
      if (s.getAttribute('data-fp-step') === step) {
        s.removeAttribute('hidden');
        s.classList.remove('as-fpStep--enter');
        void s.offsetWidth; /* restart entry animation */
        s.classList.add('as-fpStep--enter');
      } else {
        s.setAttribute('hidden', '');
      }
    }
    /* Shared "No thanks" shows on step 1 & 2, hidden on retain (retain has its
       own Keep going / Yes-just-play buttons). Use inline !important: the CSS
       .as-fpModal .as-fpCta rule sets display:block !important, so a plain
       inline display:none would lose. Inline !important beats stylesheet
       !important. */
    var noThanks = document.getElementById('startAfterPopup');
    if (noThanks) {
      if (step === 'retain' || step === 'prep2') noThanks.style.setProperty('display', 'none', 'important');
      else noThanks.style.removeProperty('display');
    }
    currentPopupStep = step;
  }

  function resetPopupToStep1() { showPopupStep('1'); }
  window.astrosongResetPopupToStep1 = resetPopupToStep1;
  function showPopupChoice() { showPopupStep('choice'); gtm('ugc_recorder_popup_choice'); }
  function chooseThisPhone() { showPopupStep('2'); gtm('ugc_recorder_device_this_phone'); }
  function chooseOtherDevice() { showPopupStep('prep2'); gtm('ugc_recorder_device_other'); }
  function popupTwoDeviceDone() {
    gtm('ugc_recorder_two_device_ready');
    /* She's set up with another device → pressing play should just work this
       load (even under ?forcepopup=). DON'T auto-play — she presses play once
       her other camera is rolling. Returns her to the normal reveal page. */
    try { var pk = revealRoot && revealRoot.dataset.pagekey; if (pk) localStorage.setItem(pk, '1'); } catch (e) {}
    if (typeof window.astrosongMarkPopupResolved === 'function') window.astrosongMarkPopupResolved();
    resetPopupToStep1();
    if (firstPlayOverlay) firstPlayOverlay.style.display = 'none';
  }
  function showPopupRetain() {
    popupStepBeforeRetain = currentPopupStep;
    showPopupStep('retain');
    gtm('ugc_recorder_popup_retain', { from: popupStepBeforeRetain });
  }
  function popupRetainStay() {
    showPopupStep(popupStepBeforeRetain || '1');
    gtm('ugc_recorder_popup_retain_stay', { to: popupStepBeforeRetain });
  }
  function popupRetainLeave() {
    gtm('ugc_recorder_popup_retain_leave');
    if (typeof window.astrosongDismissPopupAndPlay === 'function') {
      window.astrosongDismissPopupAndPlay();
    } else if (firstPlayOverlay) {
      firstPlayOverlay.style.display = 'none';
    }
  }

  function requestPermissionAndShowPreRecord() {
    gtm('ugc_recorder_permission_requested');
    /* No width/height constraints — let the browser pick the camera's
       natural FOV. Otherwise we get an over-zoomed crop on Android. */
    navigator.mediaDevices.getUserMedia({
      /* No aspectRatio constraint (forcing 9:16 digital-ZOOMs Android = over-zoom).
         On Android we DO cap resolution + fps with `max` (mode selection, NOT a
         crop → no zoom) so the encoder can sustain a clean stream — the A53
         datamoshed encoding its full-res feed. iOS handles full res fine. */
      video: IS_IOS ? { facingMode: 'user' } : {
        facingMode: 'user',
        width: { max: 1280 },
        height: { max: 1280 },
        frameRate: { ideal: 24, max: 30 },
      },
      /* MEDIA-mode audio (not "call mode"): disabling these three stops Android
         from routing through the earpiece / pumping the gain, captures the
         speaker cleanly and keeps full 44/48kHz quality. Fixes the bad
         recorded audio Lucas heard on the A53. */
      audio: {
        echoCancellation: false,
        autoGainControl: false,
        noiseSuppression: false,
      },
    }).then(function (stream) {
      gtm('ugc_recorder_permission_granted');
      state.mediaStream = stream;
      bindMirrorToStream();
      setView('pre_record');
    }).catch(function (err) {
      var name = (err && err.name) || 'unknown';
      console.error('[UGC recorder] getUserMedia failed:', err);
      gtm('ugc_recorder_permission_denied', { reason: name });
      /* Permission denied / dismissed = SHE chose → silent return, no nag. A
         hardware error (camera busy in another app, no camera found) is NOT her
         choice → tell her, so she's not left wondering why nothing happened. */
      if (name !== 'NotAllowedError' && name !== 'SecurityError') {
        recToast("Couldn't reach your camera — it might be open in another app. Close it, or tap “Upload a video”. 💛", 6000);
      }
      /* Close the recorder, return her to the base reveal page so she can play
         her AstroSong herself. Mark the popup as seen so it doesn't re-nag. */
      try {
        var pk = revealRoot && revealRoot.dataset.pagekey;
        if (pk) localStorage.setItem(pk, '1');
      } catch (e) {}
      if (typeof window.astrosongMarkPopupResolved === 'function') window.astrosongMarkPopupResolved();
      if (firstPlayOverlay) firstPlayOverlay.style.display = 'none';
      hardCloseAndReleaseStream();
    });
  }

  /* Binds the live camera stream to every mirror element on the page — the
     overlay's pre-record framing mirror AND the recording-layer face PiP.
     Document-scoped (the PiP lives outside the overlay). */
  function bindMirrorToStream() {
    var mirrors = document.querySelectorAll('[data-recorder-mirror]');
    for (var i = 0; i < mirrors.length; i++) {
      mirrors[i].srcObject = state.mediaStream;
      var p = mirrors[i].play();
      if (p && p.catch) p.catch(function () {});
    }
  }

  /* Detach the visible self-view mirror(s) from the stream so the on-screen
     preview goes dark the instant the camera track stops (no lingering last
     frame). Used as the WebCodecsEngine.stop() camera-stop callback. */
  function detachMirrorsFromStream() {
    var mirrors = document.querySelectorAll('[data-recorder-mirror]');
    for (var i = 0; i < mirrors.length; i++) {
      try { mirrors[i].pause(); mirrors[i].srcObject = null; mirrors[i].removeAttribute('src'); mirrors[i].load(); } catch (e) {}
    }
  }

  function startCountdown() {
    requestWakeLock(); /* gesture-acquired: keep the screen awake for the whole take */
    setView('countdown');
    /* Pick the capture engine and PRE-WARM it during this countdown (iOS: lazy-
       load Mediabunny + spin the OPFS worker + configure the VideoEncoder) so
       recording starts at countdown-end with NO added delay. Off-iOS this builds
       a MediaRecorderEngine whose prewarm() is a near-instant no-op. */
    selectAndPrewarmEngine();
    var el = $('[data-recorder-countdown-num]');
    /* 3-2-1, NO "GO" (validated). */
    var seq = ['3', '2', '1'];
    var idx = 0;
    function tick() {
      if (idx >= seq.length) { startRecording(); return; }
      if (el) {
        el.textContent = seq[idx];
        el.style.animation = 'none';
        void el.offsetWidth;
        el.style.animation = '';
      }
      idx++;
      setTimeout(tick, 800);
    }
    tick();
  }

  /* Build a 9:16 stream: draw the natural camera frame center-cropped onto a
     720×1280 canvas (natural scale = NO zoom), capture that + the mic audio.
     Returns the original stream as a fallback if anything is unsupported.
     [LEGACY HELPER — the v3 base records the RAW stream directly; this canvas
     path is retained verbatim only so the file stays a superset of the base.
     The active MediaRecorderEngine does NOT call it.] */
  function buildRecordStream() {
    if (typeof document.createElement('canvas').captureStream !== 'function') return state.mediaStream;
    var canvas = document.createElement('canvas');
    canvas.width = CANVAS_W; canvas.height = CANVAS_H;
    var ctx = canvas.getContext('2d');
    var src = document.createElement('video');
    src.srcObject = state.mediaStream;
    src.muted = true; src.playsInline = true; src.setAttribute('playsinline', '');
    src.style.cssText = 'position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none;';
    document.body.appendChild(src);
    var pp = src.play(); if (pp && pp.catch) pp.catch(function () {});
    state.canvas = canvas; state.srcVideo = src;

    var TARGET_AR = CANVAS_W / CANVAS_H; /* 0.5625 */
    var last = 0;
    function draw(ts) {
      state.drawRAF = requestAnimationFrame(draw);
      if (ts - last < FRAME_MS) return; /* throttle to CAP_FPS (ease the CPU on weak phones) */
      last = ts;
      var vw = src.videoWidth, vh = src.videoHeight;
      if (!vw || !vh) return;
      var srcAR = vw / vh, sx, sy, sw, sh;
      if (srcAR > TARGET_AR) { sh = vh; sw = vh * TARGET_AR; sx = (vw - sw) / 2; sy = 0; }
      else { sw = vw; sh = vw / TARGET_AR; sx = 0; sy = (vh - sh) / 2; }
      try { ctx.drawImage(src, sx, sy, sw, sh, 0, 0, CANVAS_W, CANVAS_H); } catch (e) {}
    }
    state.drawRAF = requestAnimationFrame(draw);

    try {
      var cstream = canvas.captureStream(CAP_FPS);
      var out = new MediaStream();
      cstream.getVideoTracks().forEach(function (t) { out.addTrack(t); });
      state.mediaStream.getAudioTracks().forEach(function (t) { out.addTrack(t); });
      state.canvasStream = cstream;
      state.recordStream = out;
      return out;
    } catch (e) {
      console.warn('[UGC recorder] canvas captureStream failed — recording raw', e);
      teardownCanvas();
      return state.mediaStream;
    }
  }
  function teardownCanvas() {
    if (state.drawRAF) { try { cancelAnimationFrame(state.drawRAF); } catch (e) {} state.drawRAF = null; }
    if (state.canvasStream) { try { state.canvasStream.getTracks().forEach(function (t) { try { t.stop(); } catch (e) {} }); } catch (e) {} state.canvasStream = null; }
    state.recordStream = null;
    if (state.srcVideo) { try { state.srcVideo.pause(); state.srcVideo.srcObject = null; state.srcVideo.remove(); } catch (e) {} state.srcVideo = null; }
    state.canvas = null;
  }

  /* ======================================================================== *
   *  CAPTURE ENGINE ABSTRACTION
   *  The recorder talks to ONE of these instead of touching MediaRecorder
   *  directly. Interface (all methods present on both engines):
   *    kind        'mediarecorder' | 'webcodecs'
   *    mimeExt     'mp4' | 'webm'   (preferred file extension for the output)
   *    prewarm()   -> Promise<bool> : heavy init (load deps, configure encoder).
   *                  Resolves FALSE on any failure so the recorder can swap to
   *                  MediaRecorderEngine BEFORE recording (never mid-take).
   *    start()     -> bool          : begin capture. false = construct failed
   *                  (the recorder already showed the error + hard-closed).
   *    pause()                      : pause-EXCLUDES-paused-time (so the muxed
   *                  duration + REC timer + songStartMs math stay in lockstep).
   *    resume()
   *    stop()      -> Promise<Blob> : finalize + resolve the recording Blob.
   *    discard()                    : release + DELETE any on-device scratch.
   *    hardRelease()                : release encoder/audio-recorder/source
   *                  video/mic ONLY (scratch left for the already-materialized
   *                  Blob; deleted later via discard()).
   * ======================================================================== */

  /* Feature probe for the WebCodecs path (used by webCodecsEligible). */
  var WC = {
    hasRVFC: !!(window.HTMLVideoElement && HTMLVideoElement.prototype
      && 'requestVideoFrameCallback' in HTMLVideoElement.prototype),
    hasWebCodecs: (typeof window.VideoEncoder === 'function')
      && (typeof window.VideoFrame === 'function')
      && (typeof window.EncodedVideoChunk === 'function'),
    hasOPFS: !!(navigator.storage && typeof navigator.storage.getDirectory === 'function'),
    hasWorker: (typeof window.Worker === 'function'),
    hasMediaRecorder: (typeof window.MediaRecorder === 'function'),
  };

  /* iOS-GATE: WebCodecs video capture is used ONLY on iOS (it fixes the iOS-only
     14s stall) AND only when every primitive the recipe needs is present. Off
     iOS — and old iOS lacking WebCodecs/OPFS — we keep MediaRecorder. */
  function webCodecsEligible() {
    return !!(IS_IOS
      && WC.hasWebCodecs
      && (typeof window.VideoFrame === 'function')
      && (typeof window.EncodedVideoChunk === 'function')
      && WC.hasRVFC
      && WC.hasOPFS
      && WC.hasWorker
      && WC.hasMediaRecorder);   /* needed for the audio track */
  }

  /* ---------- The hoisted shared finish handler (the base's old onstop body) ----------
   * BOTH engines resolve their Blob, then the recorder calls this. It is the v3
   * base `mediaRecorder.onstop` logic verbatim (stop tracks + open=false +
   * savePending + exit-to-overlay + wire preview + setView + gtm + clear
   * salvaging), with two tiny WebCodecs-safety guards noted inline. */
  function onRecordingFinished(blob, wasSalvaged) {
    /* One-shot: a take finishes at most once, and NEVER after a hardClose tore the
       engine down (state.engine===null). Guards the widened WebCodecs finalize
       window — a late stop() resolving after onInterrupt hard-closed must not
       save an empty blob + re-open the overlay. */
    if (state.finished) return;
    if (!state.engine) { state.finalizing = false; return; }
    state.finished = true;
    state.finalizing = false;
    /* BUG4: an interruption in the sub-second window BEFORE the first frame encodes
       (cold camera) yields an MP4 with audio but a ZERO-sample video track → a
       black/unseekable "preview". Route straight to a clean refilm instead of a
       video-less 'interrupted' card. packetsAdded===0 is ground truth; webcodecs-only. */
    if (wasSalvaged && state.engine && state.engine.kind === 'webcodecs'
        && typeof state.engine.getVideoPacketCount === 'function'
        && state.engine.getVideoPacketCount() === 0) {
      setSavingIndicator(false);
      state.recordingActive = false;
      stopCameraTracks();
      state.open = false;
      clearPartialMarker();
      gtm('ugc_recorder_salvage_empty', { engine: 'webcodecs' });
      exitRecordingModeToOverlay();
      refilmAfterInterrupt();
      return;
    }
    setSavingIndicator(false); /* hide the "Saving…" overlay shown during finalize */
    state.recordingActive = false; /* a take is no longer live (double-finish guard) */
    state.recordedBlob = blob;
    if (state.recordedUrl) URL.revokeObjectURL(state.recordedUrl);
    state.recordedUrl = blob ? URL.createObjectURL(blob) : null;
    /* Recording is done → release the camera/mic NOW (light off), but keep the
       overlay open for the send flow. From here state.open=false so the privacy
       auto-release no longer fires — backgrounding during the upload won't tear
       it down (she can switch to Insta while it sends). stopCameraTracks also
       hardRelease()s the active engine's own devices (encoder/audio recorder),
       leaving the OPFS scratch alone (the read-back Blob is already in RAM). */
    stopCameraTracks();
    state.open = false;
    /* Persist the video on-device so it's never lost (failed upload, reload,
       app switch). The card offers to resume it. Non-fatal if storage is full. */
    /* BUG1: keep the marker SET until the IDB put is DURABLY committed (savePending
       resolves on tx.oncomplete) — clearing it synchronously before commit left a
       hard-kill-during-commit window with neither a marker nor an IDB blob. Clear on
       both settle outcomes (a storage-full failure must still clear). */
    var saveP = Promise.resolve();
    try { if (typeof window.astrosongUGCSavePending === 'function' && blob) { saveP = window.astrosongUGCSavePending(blob) || Promise.resolve(); } } catch (e) {}
    Promise.resolve(saveP).then(clearPartialMarker, clearPartialMarker);
    /* FLASH FIX: the partial is now persisted (savePending above → the share-your-
       reaction card offers it on the next load). On a TRUE page-leave the page is
       dying, so do NOT render the interruption screen below — it would just flash for
       <1s before the reload kills it. Soft interruptions keep the page alive and DO
       fall through to the 'interrupted' view (correct: she's still here to see it). */
    if (unloading) { state.salvaging = false; return; }
    exitRecordingModeToOverlay();
    var preview = $('[data-recorder-preview-video]');
    if (preview && state.recordedUrl) {
      preview.src = state.recordedUrl;
      preview.load();
      wirePreviewControls(preview);
    }
    /* On an interruption, don't dump her on the Send screen — give her a clear
       choice (film again / send this). A clean finish goes straight to preview. */
    setView(wasSalvaged ? 'interrupted' : 'preview');
    gtm('ugc_recorder_finished', {
      duration_sec: state.startedAt ? Math.round((Date.now() - state.startedAt - state.pausedAccumMs) / 1000) : 0,
      file_size_mb: Math.round(((blob && blob.size) || 0) / 1048576),
      salvaged: wasSalvaged ? 1 : 0,
      engine: state.engine ? state.engine.kind : '',
    });
    /* Interruption salvage handled — clear the flag so the next take is clean. */
    state.salvaging = false;
  }

  /* ===================== MediaRecorderEngine ===============================
   * Wraps the v3 base's CURRENT record logic BYTE-BEHAVIOR-IDENTICALLY: it
   * records the RAW camera/mic stream (no canvas, no forced bitrate, no
   * timeslice), pushes ondataavailable chunks, and at stop assembles ONE
   * continuous Blob(chunks, {type:mimeType}). On Android/desktop the produced
   * bytes, mime, blob assembly, timing, and every gtm event are identical to
   * the base — it IS the base code, relocated. */
  function MediaRecorderEngine() {
    var mr = null;
    var chunks = [];
    var mimeType = null;
    var stopResolve = null;
    var stopped = false;

    return {
      kind: 'mediarecorder',
      mimeExt: (function () {
        var m = pickMimeType() || '';
        return m.indexOf('webm') > -1 ? 'webm' : 'mp4';
      })(),

      /* Nothing heavy to warm — MediaRecorder constructs instantly at start().
         Resolve true so the recorder treats it as "ready". */
      prewarm: function () { return Promise.resolve(true); },

      start: function () {
        mimeType = pickMimeType();
        state.mimeType = mimeType;
        chunks = [];
        state.recordedChunks = chunks; /* keep the legacy ref pointed at our chunks */
        stopped = false;
        /* Record the camera/mic stream DIRECTLY (no canvas). The canvas-crop 9:16
           pipeline (drawImage every frame on the main thread + captureStream +
           muxing a separate audio track) saturated weak Android (A53): corrupted
           "datamosh" video, crackling Opus audio, frozen UI, then a renderer crash.
           Native encode of the raw stream is HW-accelerated, single-clock (no A/V
           drift, clean audio) and light. 9:16 is recovered in post (UGC pipeline). */
        var recordStream = state.mediaStream;
        try {
          /* No forced bitrate. Forcing 2.5Mbps on the A53 encoder starved it (too
             little budget for the resolution → corrupt keyframes = datamosh). Let the
             encoder self-tune for the (now capped) resolution. */
          mr = new MediaRecorder(recordStream, { mimeType: mimeType });
        } catch (e) {
          console.error('[UGC recorder] MediaRecorder construct failed:', e);
          gtm('ugc_recorder_start_failed', { reason: e.message });
          alert('Recording failed to start. Try the upload option instead.');
          hardCloseAndReleaseStream();
          return false;
        }
        state.mediaRecorder = mr; /* keep state.mediaRecorder pointed at mr so the
                                     legacy nulling in hardClose still works. */
        mr.ondataavailable = function (e) {
          if (e.data && e.data.size > 0) chunks.push(e.data);
        };
        mr.onstop = function () {
          /* Same single-continuous-blob assembly the base did in onstop. */
          var blob = new Blob(chunks, { type: mimeType });
          if (stopResolve) { var r = stopResolve; stopResolve = null; r(blob); }
        };
        /* No timeslice: one continuous blob at stop, cleanly muxed. start(1000)
           forced 1s WebM clusters that glitch every ~1s in non-Chrome players
           (VLC/WMP). Same memory (we keep all data in chunks either way).
           The real cross-player fix is the server-side transcode to mp4/h264+aac
           (Android only records webm/Opus). */
        mr.start();
        return true;
      },

      /* mr.pause()/mr.resume() already EXCLUDE paused time, identical to base. */
      pause: function () {
        if (mr && mr.state === 'recording') { try { if (mr.pause) mr.pause(); } catch (e) {} }
      },
      resume: function () {
        if (mr && mr.state === 'paused') { try { if (mr.resume) mr.resume(); } catch (e) {} }
      },

      /* Resolve from mr.onstop with the single-continuous Blob assembly. If the
         recorder is already inactive, assemble immediately. */
      stop: function () {
        return new Promise(function (resolve) {
          if (stopped) { resolve(new Blob(chunks, { type: mimeType })); return; }
          stopped = true;
          stopResolve = resolve;
          try {
            if (mr && (mr.state === 'recording' || mr.state === 'paused')) {
              mr.stop();
            } else {
              stopResolve = null;
              resolve(new Blob(chunks, { type: mimeType }));
            }
          } catch (e) {
            stopResolve = null;
            resolve(new Blob(chunks, { type: mimeType }));
          }
        });
      },

      /* The recorder's stopCameraTracks/hardClose own the camera tracks, as
         before; the engine just stops its recorder + drops refs. There is no
         on-device scratch for MediaRecorder, so discard == hardRelease. */
      hardRelease: function () {
        if (mr) {
          try { if (mr.state === 'recording' || mr.state === 'paused') mr.stop(); } catch (e) {}
        }
      },
      discard: function () {
        if (mr) {
          try { if (mr.state === 'recording' || mr.state === 'paused') mr.stop(); } catch (e) {}
        }
        chunks = [];
      },
    };
  }

  /* ===================== WebCodecsEngine ===================================
   * The proven wc-av.js recipe (validated on a real iPhone, iOS 18.7), adapted
   * onto start/pause/resume/stop->Blob/discard and stripped of the harness
   * panel/log/Drive/QUIT UI. CONSUMES the existing state.mediaStream (acquired
   * once in requestPermissionAndShowPreRecord) — it NEVER calls getUserMedia, so
   * there is no second permission and no second camera grab. It draws from an
   * off-DOM, muted, off-screen <video>; the recorder's visible mirrors are
   * untouched. prewarm() does ALL heavy lifting (deps + OPFS + encoder + muxer)
   * so start() is near-instant.
   *
   * Timestamps are UNIT-CORRECT, verbatim from the recipe: captureTime &
   * expectedDisplayTime are MILLISECONDS (x1e3), mediaTime is SECONDS (x1e6),
   * strictly monotonic. Output is a fragmented MP4 (a truncated fragmented MP4
   * is a VALID playable partial — that is what makes salvage robust). */
  function WebCodecsEngine() {
    /* ---- config (the proven defaults) ---- */
    var CAP_W = 1080, CAP_H = 1920;
    var WC_FPS = 30;
    var BITRATE = 8000000;            /* 8 Mbps */
    var KEY_EVERY_MS = 2000;          /* force a keyframe ~every 2s */
    var MAX_QUEUE = 2;                /* DROP a frame when encodeQueueSize > this */
    var FRAG_MIN_SEC = 1;             /* minimumFragmentDuration (fragmented fMP4) */
    var OPFS_INIT_TIMEOUT_MS = 4000;
    var MB_IMPORT_TIMEOUT_MS = 12000;
    var OPFS_FILENAME = 'asugc-wc.mp4';
    var AVC_CODECS_HQ = ['avc1.640028', 'avc1.640032', 'avc1.4D4028', 'avc1.4D401F', 'avc1.42E01F'];
    var AUDIO_MIME_CANDIDATES = [
      'audio/mp4;codecs=mp4a.40.2', 'audio/mp4',
      'audio/webm;codecs=opus', 'audio/webm',
    ];

    /* ---- this engine's own H state object (closure-private) ---- */
    var H = {
      running: false, finishing: false,
      /* Set true by discard()/hardRelease() so a still-in-flight prewarm() chain
         (lost the countdown race, got discarded mid-warm) can bail BEFORE it
         constructs a live encoder/muxer on an already-torn engine. */
      disposed: false,

      srcVideo: null,

      /* rVFC + real timestamps */
      tsOriginUs: null, tsSource: null, lastTsUs: -1,
      firstFrameSeen: false, firstFramePerfMs: null,
      rafId: null,

      /* pause/resume — pausedBaseUs is SUBTRACTED from encode timestamps so the
         muxed video timeline EXCLUDES paused gaps (mirrors MediaRecorder.pause).
         pausedGaps[] records each {atSec,gapSec} on the VIDEO timeline so the
         audio mux can subtract the SAME gaps from audio packet timestamps even
         if iOS did not actually pause the audio MediaRecorder (proven-recipe had
         no pause; this keeps A/V in lockstep across mid-take pauses regardless).
         frameGen is a generation token: the rVFC/rAF pump only reschedules while
         its gen matches H.frameGen, so a paused loop stops and resume re-arms
         EXACTLY ONE chain (no N+1 concurrent loops). */
      paused: false, pausedAtMs: 0, pausedBaseUs: 0, pauseAtExclSec: 0,
      pausedGaps: [], frameGen: 0,

      /* encoder (video) */
      enc: null, canvas: null, ctx: null,
      encCodec: null, encAccel: null, encStartedOk: false, encErrored: null,
      keyTimerId: null, nextKeyFrame: false,

      /* mediabunny mux (video source + audio source share ONE Output) */
      MB: null, mbOut: null, mbSrc: null, mbAudioSrc: null,
      mbStarted: false, mbFinalized: false,
      decoderConfig: null, decoderCfgSeen: false, descBytes: 0,
      addChain: Promise.resolve(), addInflight: 0, addErr: null, packetsAdded: 0,

      /* audio (audio-only MediaRecorder, demuxed + muxed) */
      audioRec: null, audioMime: null, audioChunks: [], audioBlob: null,
      audioCodec: null, audioRecErr: null,
      audioStartPerfMs: null, audioFirstDataMs: null,
      videoStartPerfMs: null, avStartOffsetMs: null,
      audioPacketsAdded: 0, audioMuxErr: null, audioDemuxErr: null,

      /* The camera + mic tracks (captured from state.mediaStream at start()). The
         engine releases them ITSELF inside stop() at the proven-recipe ordering:
         camera stopped immediately, mic stopped AFTER harvestAndMuxAudio so the
         audio blob is fully captured first (a synchronously-stopped mic yields a
         silent/empty audio-only MediaRecorder on iOS). stopCameraCb lets the
         engine ask the recorder to also detach its visible mirrors when it stops
         the camera, so the self-view goes dark with the light. */
      camTrack: null, micTrack: null, camStopped: false, micStopped: false,
      stopCameraCb: null,

      /* OPFS worker */
      opfsWorker: null, opfsUrl: null, opfsReady: false,
      opfsBytesWritten: 0, opfsWriteErr: null, opfsInitErr: null, opfsFinalSize: 0,
      _writeResolvers: [], _opfsDoneCb: null,

      /* output */
      resultBlob: null,
    };

    function perfNow() { try { return performance.now(); } catch (e) { return Date.now(); } }

    /* ---- OPFS worker (dedicated worker via Blob URL — sync access handle) ----
       Owns ONE sync handle for the whole run; writes each {buf, at} at the
       muxer-given byte position. Main thread never touches OPFS. Same proven
       worker source as the recipe. */
    var OPFS_WORKER_SRC = [
      "'use strict';",
      "var handle=null, hiwater=0, fname='" + OPFS_FILENAME + "';",
      "async function init(){",
      "  if(!(self.navigator&&self.navigator.storage&&self.navigator.storage.getDirectory)) throw new Error('no navigator.storage.getDirectory in worker');",
      "  var dir=await self.navigator.storage.getDirectory();",
      "  var fh=await dir.getFileHandle(fname,{create:true});",
      "  if(typeof fh.createSyncAccessHandle!=='function') throw new Error('createSyncAccessHandle absent (no sync handle on this build)');",
      "  handle=await fh.createSyncAccessHandle();",
      "  try{ handle.truncate(0); }catch(e){}",
      "  try{ handle.flush(); }catch(e){}",
      "  hiwater=0;",
      "}",
      "self.onmessage=function(ev){",
      "  var d=ev.data||{};",
      "  if(d.cmd==='init'){",
      "    init().then(function(){ self.postMessage({type:'ready'}); })",
      "          .catch(function(err){ self.postMessage({type:'initError', reason:(err&&(err.message||err.name))||String(err)}); });",
      "    return;",
      "  }",
      "  if(d.cmd==='write'){",
      "    var buf=d.buf, at=(typeof d.at==='number')?d.at:hiwater;",
      "    if(!handle){ self.postMessage({type:'writeError', reason:'no handle', n:(buf?buf.byteLength:0)}); return; }",
      "    try{",
      "      var view=new Uint8Array(buf);",
      "      var wrote=handle.write(view,{at:at});",
      "      var end=at+wrote; if(end>hiwater) hiwater=end;",
      "      self.postMessage({type:'wrote', n:wrote, total:hiwater});",
      "    }catch(e){ self.postMessage({type:'writeError', reason:(e&&(e.message||e.name))||String(e), n:(buf?buf.byteLength:0)}); }",
      "    return;",
      "  }",
      "  if(d.cmd==='done'){",
      "    var size=hiwater;",
      "    try{ if(handle){ handle.flush(); size=handle.getSize(); } }catch(e){}",
      "    try{ if(handle){ handle.close(); } }catch(e){}",
      "    handle=null;",
      "    self.postMessage({type:'done', size:size});",
      "    return;",
      "  }",
      "  if(d.cmd==='cleanup'){",
      "    try{ if(handle){ handle.close(); } }catch(e){}",
      "    handle=null;",
      "    (async function(){",
      "      try{ var dir=await self.navigator.storage.getDirectory(); await dir.removeEntry(fname); self.postMessage({type:'cleaned'}); }",
      "      catch(e){ self.postMessage({type:'cleanError', reason:(e&&(e.message||e.name))||String(e)}); }",
      "    })();",
      "    return;",
      "  }",
      "};"
    ].join('\n');

    function startOpfsWorker() {
      return new Promise(function (resolve) {
        if (!WC.hasOPFS) { H.opfsInitErr = 'navigator.storage.getDirectory absent on main thread'; resolve(false); return; }
        if (!WC.hasWorker) { H.opfsInitErr = 'Worker constructor absent'; resolve(false); return; }
        var settled = false, toId = null;
        function finish(ok) { if (settled) return; settled = true; if (toId) { clearTimeout(toId); toId = null; } resolve(ok); }
        try {
          var blob = new Blob([OPFS_WORKER_SRC], { type: 'text/javascript' });
          H.opfsUrl = URL.createObjectURL(blob);
          H.opfsWorker = new Worker(H.opfsUrl);
        } catch (e) {
          H.opfsInitErr = 'worker construct threw: ' + (e && e.message);
          finish(false); return;
        }
        H.opfsWorker.onmessage = function (ev) {
          var d = ev.data || {};
          if (d.type === 'ready') {
            H.opfsReady = true; finish(true);
          } else if (d.type === 'initError') {
            H.opfsInitErr = d.reason || 'unknown'; finish(false);
          } else if (d.type === 'wrote') {
            H.opfsBytesWritten = d.total || H.opfsBytesWritten;
            if (H._writeResolvers.length) { var r = H._writeResolvers.shift(); r(); }
          } else if (d.type === 'writeError') {
            if (!H.opfsWriteErr) H.opfsWriteErr = d.reason || 'unknown';
            if (H._writeResolvers.length) { var r2 = H._writeResolvers.shift(); r2(); }
          } else if (d.type === 'done') {
            H.opfsFinalSize = d.size || 0;
            if (H._opfsDoneCb) { var cb = H._opfsDoneCb; H._opfsDoneCb = null; cb(); }
          } else if (d.type === 'cleaned') {
            terminateOpfsWorker();
          } else if (d.type === 'cleanError') {
            terminateOpfsWorker();
          }
        };
        H.opfsWorker.onerror = function (e) {
          H.opfsInitErr = (e && e.message) || 'worker onerror'; finish(false);
        };
        try { H.opfsWorker.postMessage({ cmd: 'init' }); } catch (e) {}
        toId = setTimeout(function () {
          if (!H.opfsReady) { H.opfsInitErr = H.opfsInitErr || ('no ready within ' + OPFS_INIT_TIMEOUT_MS + 'ms'); finish(false); }
        }, OPFS_INIT_TIMEOUT_MS);
      });
    }

    /* Positioned write with backpressure: resolves when the worker acks this
       write (so the muxer's StreamTarget can await -> bounded RAM). */
    function opfsWriteAt(u8, at) {
      if (!H.opfsReady || !H.opfsWorker) return Promise.resolve();
      return new Promise(function (resolve) {
        var ab;
        try {
          ab = u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength);
        } catch (e) {
          if (!H.opfsWriteErr) H.opfsWriteErr = 'slice threw: ' + (e && e.message);
          resolve(); return;
        }
        H._writeResolvers.push(resolve);
        try {
          H.opfsWorker.postMessage({ cmd: 'write', buf: ab, at: at }, [ab]);
        } catch (e) {
          H._writeResolvers.pop();
          if (!H.opfsWriteErr) H.opfsWriteErr = 'postMessage threw: ' + (e && e.message);
          resolve();
        }
      });
    }

    function opfsFinalize(cb) {
      if (!H.opfsWorker || !H.opfsReady) { if (cb) cb(); return; }
      H._opfsDoneCb = cb || null;
      try { H.opfsWorker.postMessage({ cmd: 'done' }); }
      catch (e) { if (H._opfsDoneCb) { var f = H._opfsDoneCb; H._opfsDoneCb = null; f(); } }
    }
    function opfsCleanupAndTerminate() {
      if (H.opfsWorker && H.opfsReady) { try { H.opfsWorker.postMessage({ cmd: 'cleanup' }); } catch (e) { terminateOpfsWorker(); } }
      else terminateOpfsWorker();
    }
    function terminateOpfsWorker() {
      if (H.opfsWorker) { try { H.opfsWorker.terminate(); } catch (e) {} H.opfsWorker = null; }
      if (H.opfsUrl) { try { URL.revokeObjectURL(H.opfsUrl); } catch (e) {} H.opfsUrl = null; }
      H.opfsReady = false; H._writeResolvers = [];
    }

    /* Read the finalized OPFS MP4 back and EAGERLY materialize it into RAM via
       file.arrayBuffer() so the returned Blob is memory-backed and independent
       of OPFS (the scratch can then be deleted without affecting it). */
    function readbackBlob() {
      return navigator.storage.getDirectory()
        .then(function (dir) { return dir.getFileHandle(OPFS_FILENAME, { create: false }); })
        .then(function (fh) { return fh.getFile(); })
        .then(function (file) { return file.arrayBuffer(); })
        .then(function (ab) {
          var blob = new Blob([ab], { type: 'video/mp4' });
          H.resultBlob = blob;
          return blob;
        });
    }

    /* ---- Mediabunny load (same-origin <script> IIFE -> window.__MB) ----
       NOT import() — iOS mis-binds ESM exports. window.require shim + cache-bust.
       The bundle assets/mediabunny.min.js is ALREADY deployed on the theme. */
    function mbAssetUrl() {
      if (!ASSET_BASE) return '';
      try {
        var u = new URL(ASSET_BASE, window.location.href);
        u.pathname = u.pathname.replace(/as-ugc-recorder\.js$/, 'mediabunny.min.js');
        u.search = '?cb=' + ((typeof Date !== 'undefined' && Date.now) ? Date.now() : '0')
          + '_' + Math.random().toString(36).slice(2, 8);
        return u.href;
      } catch (e) { return ''; }
    }

    function loadMediabunny() {
      return new Promise(function (resolve, reject) {
        if (window.__MB && typeof window.__MB.Output === 'function') { H.MB = window.__MB; resolve(window.__MB); return; }
        var url = mbAssetUrl();
        if (!url) { reject(new Error('could not derive mediabunny asset URL from as-ugc-recorder.js <script src> (ASSET_BASE empty)')); return; }
        var __setReq = false;
        try { if (typeof window.require === 'undefined') { window.require = function () { return {}; }; __setReq = true; } } catch (e) {}
        function __unshimReq() { if (__setReq) { try { delete window.require; } catch (e) { try { window.require = undefined; } catch (e2) {} } __setReq = false; } }
        var done = false;
        var to = setTimeout(function () { if (!done) { done = true; __unshimReq(); reject(new Error('Mediabunny <script> timed out (' + MB_IMPORT_TIMEOUT_MS + 'ms)')); } }, MB_IMPORT_TIMEOUT_MS);
        var sc = document.createElement('script');
        sc.src = url; sc.async = true;
        sc.onload = function () {
          if (done) return; done = true; clearTimeout(to); __unshimReq();
          var MB = window.__MB;
          if (!MB || typeof MB.Output !== 'function' || typeof MB.Mp4OutputFormat !== 'function'
            || typeof MB.EncodedVideoPacketSource !== 'function' || typeof MB.EncodedPacket !== 'function'
            || typeof MB.StreamTarget !== 'function'
            || typeof MB.EncodedAudioPacketSource !== 'function'
            || typeof MB.Input !== 'function' || typeof MB.BlobSource !== 'function'
            || typeof MB.EncodedPacketSink !== 'function' || !MB.ALL_FORMATS) {
            reject(new Error('Mediabunny loaded but window.__MB missing required exports (audio+demux)')); return;
          }
          H.MB = MB; resolve(MB);
        };
        sc.onerror = function () {
          if (done) return; done = true; clearTimeout(to); __unshimReq();
          reject(new Error('Mediabunny <script> failed to load (404 / network / CSP)'));
        };
        try { (document.head || document.documentElement).appendChild(sc); }
        catch (e) { if (!done) { done = true; clearTimeout(to); __unshimReq(); reject(new Error('Mediabunny <script> append failed: ' + (e && e.message))); } }
      });
    }

    function audioCodecFromMime(mime) {
      if (!mime) return null;
      var m = mime.toLowerCase();
      if (m.indexOf('mp4') > -1 || m.indexOf('mp4a') > -1 || m.indexOf('aac') > -1) return 'aac';
      if (m.indexOf('opus') > -1 || m.indexOf('webm') > -1) return 'opus';
      return null;
    }

    function pickAudioMime() {
      for (var i = 0; i < AUDIO_MIME_CANDIDATES.length; i++) {
        try {
          if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(AUDIO_MIME_CANDIDATES[i])) {
            return AUDIO_MIME_CANDIDATES[i];
          }
        } catch (e) {}
      }
      return null;
    }

    /* Build the Mediabunny fragmented-MP4 Output writing to OPFS, with BOTH a
       video source AND an audio source (audio packets added AFTER stop from the
       demuxed MediaRecorder blob; the source is reserved here pre-start). */
    function buildMuxer() {
      var MB = H.MB;
      var writable = new WritableStream({
        write: function (chunk) {
          if (!chunk || !chunk.data) return;
          return opfsWriteAt(chunk.data, (typeof chunk.position === 'number') ? chunk.position : H.opfsBytesWritten);
        }
      });
      var target = new MB.StreamTarget(writable);
      H.mbOut = new MB.Output({
        format: new MB.Mp4OutputFormat({ fastStart: 'fragmented', minimumFragmentDuration: FRAG_MIN_SEC }),
        target: target
      });
      H.mbSrc = new MB.EncodedVideoPacketSource('avc'); /* H.264 */
      H.mbOut.addVideoTrack(H.mbSrc, { frameRate: WC_FPS });
      H.audioCodec = audioCodecFromMime(H.audioMime);
      if (H.audioCodec) {
        H.mbAudioSrc = new MB.EncodedAudioPacketSource(H.audioCodec);
        H.mbOut.addAudioTrack(H.mbAudioSrc);
      }
      return H.mbOut.start().then(function () { H.mbStarted = true; });
    }

    /* Feed one EncodedVideoChunk (+ first-packet decoderConfig) into the muxer.
       SERIALIZE adds (decode-order requirement) via a promise chain. */
    function muxChunk(chunk, meta) {
      if (!H.mbStarted || !H.mbSrc || !H.MB) return;
      var firstMeta = null;
      if (!H.decoderCfgSeen) {
        var dc = meta && meta.decoderConfig ? meta.decoderConfig : null;
        if (!dc) { if (!H.addErr) H.addErr = 'first chunk had NO decoderConfig'; return; }
        var hasDesc = !!(dc.description && (dc.description.byteLength || dc.description.length));
        H.descBytes = hasDesc ? (dc.description.byteLength || dc.description.length) : 0;
        H.decoderConfig = {
          codec: dc.codec,
          codedWidth: dc.codedWidth || CAP_W,
          codedHeight: dc.codedHeight || CAP_H,
          description: dc.description,
          colorSpace: dc.colorSpace
        };
        H.decoderCfgSeen = true;
        if (!hasDesc) { if (!H.addErr) H.addErr = 'decoderConfig.description (avcC) MISSING'; return; }
        firstMeta = { decoderConfig: H.decoderConfig };
      }
      var pkt;
      try { pkt = H.MB.EncodedPacket.fromEncodedChunk(chunk); }
      catch (e) { if (!H.addErr) H.addErr = 'EncodedPacket.fromEncodedChunk threw: ' + (e && e.message); return; }

      H.addInflight++;
      H.addChain = H.addChain.then(function () {
        if (H.addErr) return;
        return firstMeta ? H.mbSrc.add(pkt, firstMeta) : H.mbSrc.add(pkt);
      }).then(function () {
        H.packetsAdded++;
      }, function (e) {
        if (!H.addErr) H.addErr = 'video src.add() rejected: ' + (e && (e.message || e.name));
      }).then(function () {
        H.addInflight = Math.max(0, H.addInflight - 1);
      });
    }

    /* ---- audio-only MediaRecorder (iOS audio path has no bug) ---- */
    function startAudioRecorder() {
      H.audioChunks = []; H.audioBlob = null; H.audioRecErr = null;
      H.audioStartPerfMs = null; H.audioFirstDataMs = null;
      if (!WC.hasMediaRecorder) { H.audioRecErr = 'MediaRecorder absent'; return false; }
      var atrk = state.mediaStream ? (state.mediaStream.getAudioTracks()[0] || null) : null;
      if (!atrk || atrk.readyState !== 'live') { H.audioRecErr = 'no live audio track'; return false; }
      var mime = pickAudioMime();
      H.audioMime = mime;
      if (!mime) { H.audioRecErr = 'no supported audio mime'; return false; }
      var audioStream;
      try { audioStream = new MediaStream([atrk]); }
      catch (e) { H.audioRecErr = 'MediaStream([audioTrack]) threw: ' + (e && e.message); return false; }
      try {
        H.audioRec = new MediaRecorder(audioStream, { mimeType: mime });
      } catch (e) {
        H.audioRecErr = 'MediaRecorder construct threw: ' + (e && e.message);
        return false;
      }
      H.audioRec.ondataavailable = function (ev) {
        if (H.audioFirstDataMs === null) H.audioFirstDataMs = perfNow();
        if (ev.data && ev.data.size > 0) H.audioChunks.push(ev.data);
      };
      H.audioRec.onerror = function (ev) {
        H.audioRecErr = (ev && ev.error && (ev.error.message || ev.error.name)) || 'recorder error';
      };
      try {
        H.audioRec.start();   /* no timeslice: ONE continuous blob at stop */
        H.audioStartPerfMs = perfNow();
        return true;
      } catch (e) {
        H.audioRecErr = 'recorder.start threw: ' + (e && e.message);
        return false;
      }
    }

    function stopAudioRecorder() {
      return new Promise(function (resolve) {
        if (!H.audioRec) { resolve(null); return; }
        var settled = false;
        function finish() {
          if (settled) return; settled = true;
          try { if (H.audioChunks.length) H.audioBlob = new Blob(H.audioChunks, { type: H.audioMime || 'audio/mp4' }); }
          catch (e) { H.audioRecErr = H.audioRecErr || ('blob assemble threw: ' + (e && e.message)); }
          resolve(H.audioBlob);
        }
        try { H.audioRec.addEventListener('stop', finish, { once: true }); } catch (e) {}
        try {
          if (H.audioRec.state === 'recording' || H.audioRec.state === 'paused') H.audioRec.stop();
          else finish();
        } catch (e) { finish(); }
        setTimeout(finish, 2500); /* never hang the finalize chain on a missing event */
      });
    }

    /* How much LATER audio capture began vs the video capture start (ms). >0 ⇒
       audio began after video → we ADD offset to every audio ts. <0 ⇒ trim head
       by clamping ts>=0. */
    function computeAvOffsetMs() {
      var vStart = (H.firstFramePerfMs != null) ? H.firstFramePerfMs : H.videoStartPerfMs;
      var aStart = H.audioStartPerfMs;
      if (vStart == null || aStart == null) { H.avStartOffsetMs = null; return null; }
      H.avStartOffsetMs = aStart - vStart;
      return H.avStartOffsetMs;
    }

    /* Map a CONTINUOUS-timeline audio position (seconds since video start, before
       any pause excision) onto the PAUSED-EXCLUDED video timeline by removing the
       paused gaps. Mirrors what pausedBaseUs does for video timestamps. This is
       what keeps audio in sync across a mid-take pause EVEN IF iOS did not pause
       the audio MediaRecorder (audio kept running across the gap). A packet that
       falls INSIDE a gap (paused window) is pinned to the gap's start (it is
       silence/room-noise during the pause and would otherwise overlap resume). */
    function excludePausedFromAudio(contSec) {
      var gaps = H.pausedGaps;
      if (!gaps || !gaps.length) return contSec;
      var removed = 0;
      for (var i = 0; i < gaps.length; i++) {
        var g = gaps[i];
        var gapStartCont = g.atSec + removed;       /* gap start on the continuous timeline */
        if (contSec <= gapStartCont) break;          /* packet precedes this (and all later) gaps */
        var gapEndCont = gapStartCont + g.gapSec;
        if (contSec >= gapEndCont) { removed += g.gapSec; } /* fully past the gap → drop its whole length */
        else { return g.atSec; }                     /* inside the gap → pin to its excluded start */
      }
      var out = contSec - removed;
      return out < 0 ? 0 : out;
    }

    /* Demux the MediaRecorder audio blob and add its packets to the SAME Output's
       audio source, re-based + head-offset for A/V sync. Runs AFTER video flush. */
    function muxAudioFromBlob() {
      if (!H.MB || !H.mbAudioSrc) return Promise.resolve();
      if (!H.audioBlob || H.audioBlob.size < 64) { H.audioMuxErr = 'no audio blob'; try { H.mbAudioSrc.close(); } catch (e) {} return Promise.resolve(); }
      var MB = H.MB;
      var offMs = computeAvOffsetMs();
      var offSec = (offMs != null) ? (offMs / 1000) : 0;
      var input;
      try {
        input = new MB.Input({ source: new MB.BlobSource(H.audioBlob), formats: MB.ALL_FORMATS });
      } catch (e) {
        H.audioDemuxErr = 'Input construct threw: ' + (e && e.message);
        try { H.mbAudioSrc.close(); } catch (e2) {}
        return Promise.resolve();
      }
      return input.getAudioTracks().then(function (tracks) {
        if (!tracks || !tracks.length) {
          H.audioDemuxErr = 'no audio track in MediaRecorder blob';
          try { H.mbAudioSrc.close(); } catch (e) {}
          try { input.dispose(); } catch (e) {}
          return;
        }
        var at = tracks[0];
        return at.getDecoderConfig().then(function (cfg) {
          var sink = new MB.EncodedPacketSink(at);
          var firstTs = null, first = true, count = 0;
          function pump(pkt) {
            if (!pkt) {
              try { H.mbAudioSrc.close(); } catch (e) {}
              try { input.dispose(); } catch (e) {}
              return;
            }
            if (firstTs === null) firstTs = pkt.timestamp;
            /* Continuous-timeline position (relative to video start), then remove
               the paused gaps so audio rides the SAME paused-excluded timeline as
               the video (robust whether or not iOS paused the audio recorder). */
            var shifted = excludePausedFromAudio((pkt.timestamp - firstTs) + offSec);
            if (shifted < 0) shifted = 0; /* head-trim: don't present pre-video audio */
            var newPkt;
            try {
              newPkt = pkt.clone({ timestamp: shifted });
            } catch (e) {
              try { newPkt = new MB.EncodedPacket(pkt.data, pkt.type, shifted, pkt.duration, pkt.sequenceNumber); }
              catch (e2) { if (!H.audioMuxErr) H.audioMuxErr = 'clone+construct threw: ' + (e2 && e2.message); }
            }
            if (!newPkt) { return sink.getNextPacket(pkt).then(pump); }
            var addP;
            try {
              /* First audio packet MUST carry the AudioDecoderConfig from the
                 demux (codec + sampleRate + channels + description = ASC/OpusHead). */
              addP = first ? H.mbAudioSrc.add(newPkt, cfg ? { decoderConfig: cfg } : undefined)
                           : H.mbAudioSrc.add(newPkt);
            } catch (e) {
              if (!H.audioMuxErr) H.audioMuxErr = 'audio src.add threw: ' + (e && e.message);
              try { input.dispose(); } catch (e2) {}
              return;
            }
            first = false; count++; H.audioPacketsAdded = count;
            return addP.then(function () { return sink.getNextPacket(pkt); }).then(pump, function (e) {
              if (!H.audioMuxErr) H.audioMuxErr = 'audio add/getNext rejected: ' + (e && (e.message || e.name));
              try { input.dispose(); } catch (e2) {}
            });
          }
          return sink.getFirstPacket().then(pump);
        });
      }).catch(function (e) {
        H.audioDemuxErr = H.audioDemuxErr || ('audio demux/mux chain threw: ' + (e && (e.message || e.name)));
        try { H.mbAudioSrc.close(); } catch (e2) {}
        try { input.dispose(); } catch (e2) {}
      });
    }

    /* ---- source video (off-DOM, muted, off-screen) the encoder draws from ----
       The recorder's visible mirrors are untouched. We attach the SAME
       state.mediaStream (no second getUserMedia). */
    function ensureSourceVideo() {
      if (H.srcVideo) {
        try { if (H.srcVideo.srcObject !== state.mediaStream) { H.srcVideo.srcObject = state.mediaStream; var pp0 = H.srcVideo.play(); if (pp0 && pp0.catch) pp0.catch(function () {}); } } catch (e) {}
        return;
      }
      var v = document.createElement('video');
      v.muted = true; v.playsInline = true;
      v.setAttribute('playsinline', ''); v.setAttribute('muted', '');
      v.autoplay = true;
      v.style.cssText = 'position:fixed;left:-9999px;top:0;width:2px;height:2px;opacity:0;pointer-events:none;';
      try { v.srcObject = state.mediaStream; } catch (e) {}
      document.body.appendChild(v);
      var pp = v.play(); if (pp && pp.catch) pp.catch(function () {});
      H.srcVideo = v;
    }
    function detachSourceVideo() {
      if (!H.srcVideo) return;
      try { H.srcVideo.pause(); H.srcVideo.srcObject = null; H.srcVideo.removeAttribute('src'); H.srcVideo.load(); H.srcVideo.remove(); } catch (e) {}
      H.srcVideo = null;
    }

    /* ---- encoder (video) ---- */
    function buildEncoderConfig(codec, accel) {
      var cfg = {
        codec: codec,
        width: CAP_W, height: CAP_H,
        bitrate: BITRATE,
        framerate: WC_FPS,
        latencyMode: 'realtime',
        avc: { format: 'avc' }   /* CRITICAL: 'avc' => emits avcC description (REQUIRED for a playable MP4). */
      };
      if (accel && accel !== 'no-preference') cfg.hardwareAcceleration = accel;
      return cfg;
    }
    function pickSupportedConfig() {
      var accels = ['prefer-hardware', 'no-preference', 'prefer-software'];
      var combos = [];
      for (var a = 0; a < accels.length; a++) for (var c = 0; c < AVC_CODECS_HQ.length; c++) combos.push({ codec: AVC_CODECS_HQ[c], accel: accels[a] });
      var i = 0;
      function next() {
        if (i >= combos.length) return Promise.resolve(null);
        var combo = combos[i++];
        var cfg = buildEncoderConfig(combo.codec, combo.accel);
        var probe;
        try { probe = window.VideoEncoder.isConfigSupported(cfg); } catch (e) { return next(); }
        if (!probe || typeof probe.then !== 'function') return next();
        return probe.then(function (res) {
          if (res && res.supported) return { codec: combo.codec, accel: combo.accel, config: cfg };
          return next();
        }).catch(function () { return next(); });
      }
      return next();
    }
    function startEncoder() {
      return new Promise(function (resolve) {
        if (H.disposed) { resolve(false); return; }   /* torn mid-prewarm → do NOT build a live encoder */
        if (!WC.hasWebCodecs) { resolve(false); return; }
        H.canvas = document.createElement('canvas');
        H.canvas.width = CAP_W; H.canvas.height = CAP_H;
        try { H.ctx = H.canvas.getContext('2d', { alpha: false, desynchronized: true }); } catch (e) { H.ctx = H.canvas.getContext('2d'); }
        if (!H.ctx) { resolve(false); return; }
        pickSupportedConfig().then(function (sel) {
          if (H.disposed) { resolve(false); return; }   /* torn during the isConfigSupported cascade */
          if (!sel) { resolve(false); return; }
          H.encCodec = sel.codec; H.encAccel = sel.accel;
          var enc;
          try {
            enc = new window.VideoEncoder({
              output: onEncoderOutput,
              error: function (err) { H.encErrored = (err && (err.message || err.name)) || String(err); }
            });
          } catch (e) { resolve(false); return; }
          H.enc = enc;
          try {
            enc.configure(sel.config); H.encStartedOk = true;
          } catch (e) {
            try {
              var soft = buildEncoderConfig('avc1.4D401F', 'prefer-software');
              enc.configure(soft); H.encCodec = 'avc1.4D401F'; H.encAccel = 'prefer-software'; H.encStartedOk = true;
            } catch (e2) { resolve(false); return; }
          }
          /* If we got disposed while configuring, close the encoder we just built
             so no live HW session is left running during her real (MR) take. */
          if (H.disposed) { try { if (enc.state !== 'closed') enc.close(); } catch (e3) {} H.enc = null; H.encStartedOk = false; resolve(false); return; }
          resolve(true);
        });
      });
    }

    /* UNIT-CORRECT conversion to microseconds. captureTime & expectedDisplayTime
       are DOMHighResTimeStamp = MILLISECONDS (x1e3). mediaTime is SECONDS (x1e6).
       pausedBaseUs is subtracted so paused gaps are excluded from the timeline. */
    function encodeTimestampUs(now, meta) {
      var rawUs = null, src = null;
      if (meta) {
        if (typeof meta.captureTime === 'number') { rawUs = Math.round(meta.captureTime * 1e3); src = 'captureTime'; }
        else if (typeof meta.mediaTime === 'number') { rawUs = Math.round(meta.mediaTime * 1e6); src = 'mediaTime'; }
        else if (typeof meta.expectedDisplayTime === 'number') { rawUs = Math.round(meta.expectedDisplayTime * 1e3); src = 'expectedDisplayTime'; }
      }
      if (rawUs === null) { rawUs = Math.round((typeof now === 'number' ? now : performance.now()) * 1e3); src = 'perf'; }
      if (H.tsOriginUs === null) {
        H.tsOriginUs = rawUs; H.tsSource = src;
      } else if (src !== H.tsSource) {
        /* Rebase so the NEXT computed tsUs continues from H.lastTsUs. Since the
           final tsUs = rawUs - tsOriginUs - pausedBaseUs, fold pausedBaseUs into
           the rebase too (else a source switch AFTER a pause double-counts it). */
        H.tsOriginUs = rawUs - H.pausedBaseUs - (H.lastTsUs >= 0 ? H.lastTsUs : 0); H.tsSource = src;
      }
      var tsUs = rawUs - H.tsOriginUs - H.pausedBaseUs;
      if (tsUs <= H.lastTsUs) tsUs = H.lastTsUs + 1; /* strictly monotonic */
      H.lastTsUs = tsUs;
      return tsUs;
    }

    /* Arm ONE frame-pump chain tagged with generation `gen`. A loop reschedules
       ONLY while its gen still matches H.frameGen AND we are not paused — so on
       pause the loop STOPS rescheduling (self-cancels) and resume bumps frameGen
       + arms exactly one fresh chain. This prevents the N+1 concurrent rVFC loops
       the proven recipe never had (it had no pause). */
    function armFrameWatch(gen) {
      if (WC.hasRVFC && H.srcVideo) {
        var cb = function (now, meta) {
          if (!H.running || H.paused || gen !== H.frameGen) return; /* stale/paused → stop */
          if (!H.firstFrameSeen) { H.firstFrameSeen = true; H.firstFramePerfMs = perfNow(); }
          pumpFrame(now, meta);
          try { H.srcVideo.requestVideoFrameCallback(cb); } catch (e) {}
        };
        try { H.srcVideo.requestVideoFrameCallback(cb); } catch (e) {}
      } else if (H.srcVideo) {
        var lastCT = -1;
        var raf = function () {
          if (!H.running || H.paused || gen !== H.frameGen) { H.rafId = null; return; } /* stale/paused → stop */
          var ct = H.srcVideo ? (H.srcVideo.currentTime || 0) : 0;
          if (ct !== lastCT) { if (!H.firstFrameSeen) { H.firstFrameSeen = true; H.firstFramePerfMs = perfNow(); } lastCT = ct; pumpFrame(performance.now(), null); }
          H.rafId = requestAnimationFrame(raf);
        };
        H.rafId = requestAnimationFrame(raf);
      }
    }
    function startFrameWatch() {
      H.tsOriginUs = null; H.tsSource = null; H.lastTsUs = -1;
      H.firstFrameSeen = false; H.firstFramePerfMs = null;
      H.frameGen++;            /* invalidate any prior chain */
      armFrameWatch(H.frameGen);
    }

    /* Draw one freshly-presented camera frame to the 9:16 canvas (center-crop,
       cover, NON-mirrored — recovered in post like the base), wrap as a
       VideoFrame with the REAL rVFC timestamp, encode, ALWAYS close. DROP this
       frame when encodeQueueSize>limit (backpressure). pumpFrame guards on
       videoWidth>0 and simply skips until the stream is live (so the first
       sub-100ms of frames may be dropped if the camera isn't warm yet). */
    function pumpFrame(now, meta) {
      if (!H.running || H.paused) return;
      if (!H.enc || H.enc.state !== 'configured' || !H.encStartedOk) return;
      var v = H.srcVideo;
      if (!v || !(v.videoWidth > 0 && v.videoHeight > 0)) return; /* skip until the stream is live */
      var q = H.enc.encodeQueueSize;
      if (q > MAX_QUEUE) return; /* drop frame (backpressure) */
      try {
        var sw = v.videoWidth, sh = v.videoHeight, dw = CAP_W, dh = CAP_H;
        var sAR = sw / sh, dAR = dw / dh, cx, cy, cw, ch;
        if (sAR > dAR) { ch = sh; cw = Math.round(sh * dAR); cx = Math.round((sw - cw) / 2); cy = 0; }
        else { cw = sw; ch = Math.round(sw / dAR); cx = 0; cy = Math.round((sh - ch) / 2); }
        H.ctx.drawImage(v, cx, cy, cw, ch, 0, 0, dw, dh);
      } catch (e) { return; }
      var ts = encodeTimestampUs(now, meta);
      var frame;
      try { frame = new window.VideoFrame(H.canvas, { timestamp: ts }); } catch (e) { return; }
      var key = H.nextKeyFrame; H.nextKeyFrame = false;
      try { H.enc.encode(frame, { keyFrame: key }); }
      catch (e) {}
      finally { try { frame.close(); } catch (e) {} } /* ALWAYS close the frame */
    }

    function onEncoderOutput(chunk, meta) {
      if (H.mbStarted && !H.addErr) {
        try { muxChunk(chunk, meta); }
        catch (e) { if (!H.addErr) H.addErr = 'muxChunk threw: ' + (e && e.message); }
      }
    }

    /* ===================== ENGINE INTERFACE METHODS ===================== */

    /* prewarm: ALL heavy lifting so start() is near-instant. Resolves FALSE on
       any failure and releases what it built — the recorder then swaps to
       MediaRecorderEngine BEFORE recording (never mid-take). */
    function prewarm() {
      if (!webCodecsEligible()) return Promise.resolve(false);
      if (H.disposed) return Promise.resolve(false);
      ensureSourceVideo();
      /* A disposed-check between EVERY async stage so a prewarm that lost the
         countdown race (and got discard()'d) bails BEFORE building a live encoder
         or muxer on a torn engine. startEncoder also guards internally. */
      return startOpfsWorker().then(function (opfsOk) {
        if (H.disposed) throw new Error('disposed');
        if (!opfsOk) throw new Error('OPFS worker failed: ' + (H.opfsInitErr || 'unknown'));
        return loadMediabunny();
      }).then(function () {
        if (H.disposed) throw new Error('disposed');
        H.audioMime = pickAudioMime(); /* decide audio codec before buildMuxer */
        return startEncoder();
      }).then(function (encOk) {
        if (H.disposed) throw new Error('disposed');
        if (!encOk) throw new Error('encoder failed to configure');
        return buildMuxer();
      }).then(function () {
        if (H.disposed) throw new Error('disposed');
        return true;
      }).catch(function (err) {
        /* release whatever was built; the recorder will fall back */
        try { closeEncoder(); } catch (e) {}
        try { opfsCleanupAndTerminate(); } catch (e) {}
        try { detachSourceVideo(); } catch (e) {}
        H.encStartedOk = false;
        if (window.console && console.warn) { try { console.warn('[UGC recorder] WebCodecs prewarm failed:', err && (err.message || err)); } catch (e) {} }
        return false;
      });
    }

    function start(opts) {
      if (H.disposed) return false; /* a discard/hardRelease already tore us down */
      if (!H.mbStarted || !H.enc || !H.encStartedOk) return false; /* not prewarmed */
      ensureSourceVideo();
      /* Grab the camera + mic tracks so stop() can release them at the correct
         points (camera now, mic post-audio-harvest). stopCameraCb lets the engine
         detach the recorder's visible mirrors when it stops the camera. */
      try {
        var vts = state.mediaStream ? state.mediaStream.getVideoTracks() : [];
        var ats = state.mediaStream ? state.mediaStream.getAudioTracks() : [];
        H.camTrack = vts && vts.length ? vts[0] : null;
        H.micTrack = ats && ats.length ? ats[0] : null;
      } catch (e) {}
      H.camStopped = false; H.micStopped = false;
      H.stopCameraCb = (opts && typeof opts.stopCameraCb === 'function') ? opts.stopCameraCb : null;
      H.running = true;
      H.paused = false; H.pausedAtMs = 0; H.pausedBaseUs = 0; H.pauseAtExclSec = 0; H.pausedGaps = [];
      H.videoStartPerfMs = perfNow();
      H.nextKeyFrame = true;
      H.keyTimerId = setInterval(function () { if (H.running && !H.paused) H.nextKeyFrame = true; }, KEY_EVERY_MS);
      /* Start audio + video as close together as possible: audio recorder first
         (its start() returns ~instantly), then the frame watch begins encoding. */
      startAudioRecorder();
      startFrameWatch();
      return true;
    }

    function pause() {
      if (!H.running || H.paused) return;
      H.paused = true;
      /* BUG2: capture the paused-EXCLUDED content position (the last emitted video
         ts, which already excludes prior gaps) where THIS pause begins, so the audio
         mux bins the gap at the right place. (pausedBaseUs is 0 at the first pause →
         using it mis-binned every audio packet → progressive A/V desync per pause.) */
      H.pauseAtExclSec = (H.lastTsUs >= 0 ? H.lastTsUs : 0) / 1e6;
      H.pausedAtMs = perfNow();
      /* Bump the generation so the live rVFC/rAF loop stops rescheduling (it will
         see gen !== H.frameGen on its next tick and self-cancel). */
      H.frameGen++;
      /* Pause the audio recorder so its duration matches the (paused) video IF
         iOS honors it. We do NOT rely on this: muxAudioFromBlob ALSO subtracts the
         same paused gaps from audio timestamps, so audio stays in sync even when
         iOS keeps the audio recorder running across the gap (proven-recipe never
         exercised pause; this makes pause robust either way). */
      try { if (H.audioRec && H.audioRec.state === 'recording' && H.audioRec.pause) H.audioRec.pause(); } catch (e) {}
    }

    function resume() {
      if (!H.running || !H.paused) return;
      /* Fold the paused wall-clock into pausedBaseUs (microseconds) SUBTRACTED
         from encode timestamps so the muxed video timeline excludes the gap —
         matching MediaRecorder.pause() semantics + the recorder's pause-aware
         elapsed clock + songStartMs. */
      var gapMs = perfNow() - (H.pausedAtMs || perfNow());
      if (gapMs < 0) gapMs = 0;
      /* Record the gap on the VIDEO timeline (atSec = excluded-time position BEFORE
         this gap; gapSec = its length) so muxAudioFromBlob can subtract it from
         audio packets whose raw timestamp falls after this point. */
      var atSec = H.pauseAtExclSec || 0;
      H.pausedGaps.push({ atSec: atSec, gapSec: gapMs / 1000 });
      H.pausedBaseUs += Math.round(gapMs * 1e3);
      H.paused = false;
      H.pausedAtMs = 0;
      H.nextKeyFrame = true; /* a fresh keyframe right after the gap */
      try { if (H.audioRec && H.audioRec.state === 'paused' && H.audioRec.resume) H.audioRec.resume(); } catch (e) {}
      /* Re-arm EXACTLY ONE fresh frame-pump chain (the paused loop self-cancelled). */
      H.frameGen++;
      H.rafId = null;
      armFrameWatch(H.frameGen);
    }

    /* stop -> finalize the fragmented MP4 from OPFS and resolve a memory-backed
       Blob. Double-stop guarded by H.finishing. The catch() still attempts a
       readback so even a finalize error yields the partial.
       PRIVACY ORDERING (ported from the proven recipe): the CAMERA track is
       stopped IMMEDIATELY (light off the instant Finish/salvage fires); the MIC
       track is stopped only AFTER harvestAndMuxAudio so the no-timeslice audio
       blob is captured first (a synchronously-killed mic = silent partial). */
    function stop() {
      return new Promise(function (resolve) {
        if (H.finishing) {
          /* a stop is already draining — resolve with the SAME result it produces,
             never a racing 0-byte blob. Prefer the materialized blob; otherwise
             wait (bounded) for finishing to clear, then take resultBlob. */
          if (H.resultBlob) { resolve(H.resultBlob); return; }
          var waited = 0;
          var iv = setInterval(function () {
            waited += 100;
            if (H.resultBlob) { clearInterval(iv); resolve(H.resultBlob); return; }
            if (!H.finishing || waited >= 8000) {
              clearInterval(iv);
              if (!H.resultBlob && window.console && console.warn) { try { console.warn('[UGC recorder] stop() re-entry resolved with no resultBlob'); } catch (e) {} }
              resolve(H.resultBlob || new Blob([], { type: 'video/mp4' }));
            }
          }, 100);
          return;
        }
        H.finishing = true;
        H.running = false;
        if (H.keyTimerId) { clearInterval(H.keyTimerId); H.keyTimerId = null; }
        if (H.rafId) { try { cancelAnimationFrame(H.rafId); } catch (e) {} H.rafId = null; }
        /* Camera off NOW (privacy: do not hold the light through the multi-second
           finalize). Mic stays live until the audio blob is harvested below. */
        stopCameraTrackNow();
        /* BUG3 PRIVACY BELT: guarantee the mic dies within 20s no matter what — even
           if flush/drain below wedge under iOS pressure (a stranded orange mic dot is
           a privacy failure Lucas treats as critical). Idempotent (micStopped guard). */
        setTimeout(function () { try { stopMicTrackNow(); } catch (e) {} }, 20000);

        function flushEncoder() {
          return new Promise(function (res) {
            if (H.enc && H.enc.state === 'configured') {
              try { H.enc.flush().then(function () {}, function () {}).then(res); }
              catch (e) { res(); }
            } else res();
          });
        }
        function drainVideoAdds() { return H.addChain.then(function () {}, function () {}); }
        function harvestAndMuxAudio() {
          return stopAudioRecorder().then(function () { return muxAudioFromBlob(); });
        }
        /* Mic is now safe to release — its buffer is harvested + muxed. (Proven
           recipe step 4: releaseTracks AFTER harvest, BEFORE finalize/readback.) */
        function releaseMic() { stopMicTrackNow(); return Promise.resolve(); }
        function finalizeMux() {
          return new Promise(function (res) {
            if (!H.mbOut || !H.mbStarted || H.mbFinalized) { res(); return; }
            try {
              H.mbOut.finalize().then(function () { H.mbFinalized = true; }, function () {}).then(res);
            } catch (e) { res(); }
          });
        }
        function finalizeOpfs() { return new Promise(function (res) { opfsFinalize(res); }); }
        /* BUG3: bound the two stages that can wedge under iOS pressure (a stalled
           VideoEncoder.flush or an unacked OPFS write) so releaseMic is ALWAYS
           reached. Resolves (never rejects) on timeout — a fragmented MP4 truncated
           at a fragment boundary stays playable: trade a few trailing frames for a
           guaranteed mic release (reliability > quality). harvestAndMuxAudio keeps
           its OWN full window (stage 3, its own 2.5s recorder timeout) so audio is
           never cut short → no silent partial. */
        function withTimeout(p, ms) {
          return new Promise(function (res) {
            var done = false;
            var t = setTimeout(function () { if (!done) { done = true; res(); } }, ms);
            Promise.resolve(p).then(function () { if (!done) { done = true; clearTimeout(t); res(); } },
                                    function () { if (!done) { done = true; clearTimeout(t); res(); } });
          });
        }

        withTimeout(flushEncoder(), 6000)
          .then(function () { return withTimeout(drainVideoAdds(), 6000); })
          .then(harvestAndMuxAudio)
          .then(releaseMic)
          .then(finalizeMux)
          .then(finalizeOpfs)
          .then(function () {
            closeEncoder();
            return readbackBlob();
          })
          .then(function (blob) {
            H.finishing = false;
            resolve(blob);
          })
          .catch(function () {
            /* finalize error — still try a readback so a partial isn't lost */
            readbackBlob().then(function (blob) {
              H.finishing = false; resolve(blob);
            }).catch(function () {
              H.finishing = false; resolve(new Blob([], { type: 'video/mp4' }));
            });
          });
      });
    }

    function closeEncoder() {
      if (H.enc) { try { if (H.enc.state !== 'closed') H.enc.close(); } catch (e) {} }
      H.enc = null;
    }
    function stopAudioRecHard() {
      if (H.audioRec) {
        try { if (H.audioRec.state === 'recording' || H.audioRec.state === 'paused') H.audioRec.stop(); } catch (e) {}
        H.audioRec = null;
      }
    }

    /* Stop the CAMERA track the instant recording ends (privacy: light off NOW)
       and ask the recorder to detach its visible self-view mirrors so the screen
       goes dark with the light. The mic is left LIVE here — it is stopped only
       after harvestAndMuxAudio so its buffered audio is captured first. */
    function stopCameraTrackNow() {
      if (H.camStopped) return;
      H.camStopped = true;
      if (H.camTrack) { try { H.camTrack.stop(); } catch (e) {} }
      if (H.stopCameraCb) { try { H.stopCameraCb(); } catch (e) {} }
    }
    /* Stop the MIC track. Called AFTER the audio blob is harvested + muxed (the
       proven recipe's releaseTracks-after-harvest ordering) so iOS Safari has
       already delivered the no-timeslice dataavailable. */
    function stopMicTrackNow() {
      if (H.micStopped) return;
      H.micStopped = true;
      if (H.micTrack) { try { H.micTrack.stop(); } catch (e) {} }
    }

    /* hardRelease: release encoder + audio recorder + source video + the engine's
       own camera/mic track refs ONLY. The OPFS scratch is left intact (the
       read-back Blob is already in RAM on the clean-finish path; the scratch is
       deleted later by discard()). Sets H.disposed so any in-flight prewarm()
       chain bails instead of building a live encoder on a torn engine. */
    function hardRelease() {
      H.disposed = true;
      H.running = false; H.paused = false; H.frameGen++;
      if (H.keyTimerId) { clearInterval(H.keyTimerId); H.keyTimerId = null; }
      if (H.rafId) { try { cancelAnimationFrame(H.rafId); } catch (e) {} H.rafId = null; }
      closeEncoder();
      stopAudioRecHard();
      detachSourceVideo();
      /* The recorder's stopCameraTracks/hardClose still owns + stops the shared
         stream's tracks; stopping our refs here is a belt-and-suspenders release
         on the engine's own teardown paths. */
      stopCameraTrackNow();
      stopMicTrackNow();
    }

    /* discard: hardRelease + DELETE the OPFS scratch MP4 (privacy / restart). */
    function discard() {
      hardRelease();
      opfsCleanupAndTerminate();
    }

    return {
      kind: 'webcodecs',
      mimeExt: 'mp4',
      prewarm: prewarm,
      start: start,
      pause: pause,
      resume: resume,
      stop: stop,
      discard: discard,
      hardRelease: hardRelease,
      /* BUG4: video packets actually muxed so far (0 = interrupted before the first
         frame encoded → a video-less partial). webcodecs-only; MediaRecorder's
         absence of this method = "has video" so the Android/desktop path is untouched. */
      getVideoPacketCount: function () { return H.packetsAdded; },
    };
  }

  /* Pick the engine for this take and PRE-WARM it. iOS-eligible -> WebCodecs;
     otherwise -> MediaRecorder. If the WebCodecs prewarm fails for ANY reason,
     swap to MediaRecorderEngine BEFORE recording starts (never mid-take). Called
     at the top of startCountdown() so the warm-up overlaps the 3-2-1. Idempotent
     within a take: a second call (belt-and-suspenders from startRecording) is a
     no-op if an engine is already selected. */
  function selectAndPrewarmEngine() {
    if (state.engine) return; /* already selected for this take */
    if (webCodecsEligible()) {
      var wc = WebCodecsEngine();
      state.engine = wc;
      gtm('ugc_recorder_engine_selected', { engine: 'webcodecs' });
      try {
        wc.prewarm().then(function (ok) {
          if (!ok) {
            /* fell back during warm-up (before recording) — swap to MediaRecorder */
            if (state.engine === wc && !state.recordingActive) {
              try { wc.discard(); } catch (e) {}
              state.engine = MediaRecorderEngine();
              gtm('ugc_recorder_engine_fallback', { from: 'webcodecs', stage: 'prewarm' });
            }
          }
        }).catch(function () {
          if (state.engine === wc && !state.recordingActive) {
            try { wc.discard(); } catch (e) {}
            state.engine = MediaRecorderEngine();
            gtm('ugc_recorder_engine_fallback', { from: 'webcodecs', stage: 'prewarm_throw' });
          }
        });
      } catch (e) {
        try { wc.discard(); } catch (e2) {}
        state.engine = MediaRecorderEngine();
        gtm('ugc_recorder_engine_fallback', { from: 'webcodecs', stage: 'prewarm_sync' });
      }
    } else {
      state.engine = MediaRecorderEngine();
      gtm('ugc_recorder_engine_selected', { engine: 'mediarecorder' });
      try { state.engine.prewarm(); } catch (e) {}
    }
  }

  function startRecording() {
    state.recordedChunks = [];
    state.salvaging = false;
    state.finished = false;     /* fresh take → re-arm the one-shot finish latch */
    state.finalizing = false;
    /* Belt-and-suspenders: the countdown pre-warms the engine, but if we somehow
       arrived without one, select+prewarm now (synchronous build; WebCodecs
       prewarm is async but start() guards on mbStarted/encoder readiness and
       returns false if not ready, triggering the one-time fallback below). */
    if (!state.engine) selectAndPrewarmEngine();

    /* The engine releases the camera track itself inside stop() (light off the
       instant Finish/salvage fires); this callback detaches the visible mirrors
       at the same moment so the self-view goes dark with the light. */
    var startOpts = { stopCameraCb: detachMirrorsFromStream };
    var ok;
    try {
      ok = state.engine.start(startOpts);
    } catch (e) {
      ok = false;
    }
    /* One-time WebCodecs -> MediaRecorder fallback if start() failed/threw AND a
       take is not already live. The swap never happens mid-take. (MediaRecorder
       construct-fail already hard-closed + returned false from its own start.) */
    if (ok === false && state.engine && state.engine.kind === 'webcodecs' && !state.recordingActive) {
      try { state.engine.discard(); } catch (e) {}
      state.engine = MediaRecorderEngine();
      gtm('ugc_recorder_engine_fallback', { from: 'webcodecs', stage: 'start' });
      try { ok = state.engine.start(startOpts); } catch (e) { ok = false; }
    }
    if (ok === false) return; /* the engine (MediaRecorder) already alerted + hard-closed */

    state.recordingActive = true;
    /* BUG1: a webcodecs take now owns the OPFS scratch — mark it so a hard-unload
       BEFORE finalize can be recovered on the next load. Cleared on every terminal path. */
    if (state.engine && state.engine.kind === 'webcodecs') setPartialMarker();
    state.startedAt = Date.now();
    state.elapsedTimerId = setInterval(updateElapsed, 500);
    /* Recording is live → phase 1 "introduce yourself" (clean audio, no song).
       REC + STOP appear here as PERSISTENT chrome and never move from now until
       she stops — across the speak phase AND the song phase. */
    enterSpeakPhase();
    gtm('ugc_recorder_started', { engine: state.engine ? state.engine.kind : '' });
  }

  /* ---------- Recording chrome (REC + STOP persistent across both phases) ----------
   * Phase 1 = speak panel (her face big, recording, song NOT playing).
   * Phase 2 = the real reveal page (vinyl/lyrics) + song. REC + STOP stay put. */
  function enterSpeakPhase() {
    /* Leave the Get-ready/countdown overlay; the recording layer takes over. */
    overlay.removeAttribute('data-open');
    document.body.classList.remove('as-recorder-active');
    if (firstPlayOverlay) firstPlayOverlay.style.display = 'none';
    /* Mark the popup as seen so the real play button plays directly (no popup). */
    try {
      var pk = revealRoot && revealRoot.dataset.pagekey;
      if (pk) localStorage.setItem(pk, '1');
    } catch (e) {}
    state.view = 'speak';
    state.songStarted = false;
    state.pausedAccumMs = 0;
    state.pausedAt = null;
    state.warned = false;
    state.autoStopped = false;
    document.body.classList.add('as-recording');  /* show REC + STOP chrome */
    document.body.classList.add('as-rec-speak');   /* speak panel up; reveal page + PiP hidden */
    showRecPauseModal(false);
    bindMirrorToStream();
    updateElapsed();
    try { window.scrollTo(0, 0); } catch (e) {}
  }

  /* Phase 2: REVEAL MY ASTROSONG → drop the speak panel, show the real reveal
     page, auto-play. REC + STOP do not move. Synchronous from her tap so
     audio.play() keeps the user gesture (iOS). */
  function launchSong() {
    /* BUG5: REVEAL (speak-phase CTA) must be inert once a take is finalizing/salvaging
       or no longer live — returning to a salvage window must not re-blast the song. */
    if (state.finalizing || state.salvaging || !state.recordingActive) return;
    document.body.classList.remove('as-rec-speak');
    state.view = 'recording';
    state.songStarted = true;
    /* Offset of the song start within the recorded video = the REVEAL tap moment
       (recorder controls it, so it's exact). Excludes paused time so it maps to the
       recorded timeline. Sent to the worker -> Drive so the Factory gets sample-accurate
       sync without acoustic detection. Engine-independent (Date.now pause-aware). */
    state.songStartMs = state.startedAt ? (Date.now() - state.startedAt - state.pausedAccumMs) : null;
    bindMirrorToStream(); /* the small face PiP now shows */
    try { window.scrollTo(0, 0); } catch (e) {}
    /* REVEAL always starts the song from the BEGINNING (a fresh take / re-record
       must not resume mid-song where a previous take left off). Pause/resume
       within a take keeps its position — that path doesn't go through here. */
    try { if (songAudio) songAudio.currentTime = 0; } catch (e) {}
    if (typeof window.astrosongPlaySong === 'function') {
      window.astrosongPlaySong();
    } else if (songAudio) {
      var p = songAudio.play();
      if (p && p.catch) p.catch(function (e) { console.error('[UGC recorder] song play failed:', e); });
    }
    gtm('ugc_recorder_song_launched');
  }

  function exitRecordingModeToOverlay() {
    document.body.classList.remove('as-recording');
    document.body.classList.remove('as-rec-speak');
    showRecPauseModal(false);
    document.body.classList.add('as-recorder-active');
    overlay.setAttribute('data-open', 'true');
  }

  function showRecPauseModal(show) {
    var m = $L('[data-rec-pausemodal]');
    if (m) m.style.display = show ? 'flex' : 'none';
  }

  function updateElapsed() {
    if (!state.startedAt) return;
    var total = Math.floor((Date.now() - state.startedAt - state.pausedAccumMs) / 1000);
    if (total < 0) total = 0;
    var m = Math.floor(total / 60);
    var s = total % 60;
    var txt = m + ':' + (s < 10 ? '0' + s : s);
    /* Update every REC timer (the speak-screen pill AND the reveal-layer pill)
       so the count is continuous across the transition. */
    var els = document.querySelectorAll('[data-rec-elapsed]');
    for (var i = 0; i < els.length; i++) els[i].textContent = txt;

    /* Long-video safety */
    if (total >= REC_MAX_SEC && !state.autoStopped) {
      state.autoStopped = true;
      recToast("We've saved your video at the 8-minute limit 💛", 6000);
      finalizeStop(); /* → preview, nothing lost */
    } else if (total >= REC_WARN_SEC && !state.warned) {
      state.warned = true;
      recToast('About a minute left — wrap up whenever you like 💛', 6000);
    }
  }

  /* Lightweight non-blocking toast over the recording chrome. */
  var recToastTimer = null;
  function recToast(msg, ms) {
    var el = document.getElementById('asRecToast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'asRecToast';
      el.className = 'as-rec-toast';
      document.body.appendChild(el);
    }
    el.textContent = msg;
    el.setAttribute('data-show', 'true');
    if (recToastTimer) clearTimeout(recToastTimer);
    recToastTimer = setTimeout(function () { el.removeAttribute('data-show'); }, ms || 3500);
  }
  function fmt(sec) {
    if (!isFinite(sec)) return '0:00';
    var s = Math.floor(sec);
    var m = Math.floor(s / 60);
    s = s % 60;
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  /* ---------- Custom preview controls ----------
   * Native <video controls> is browser-ugly. We render our own minimal
   * play/scrub UI matching the gold/midnight aesthetic. */
  function wirePreviewControls(video) {
    var wrap = $('[data-recorder-preview-wrap]');
    var overlayEl = $('[data-recorder-preview-overlay]');
    var scrub = $('[data-recorder-preview-scrub]');
    var timeLabel = $('[data-recorder-preview-time]');
    if (!wrap || !overlayEl || !scrub || !timeLabel) return;

    function setPlaying(p) {
      if (p) wrap.setAttribute('data-playing', 'true');
      else wrap.removeAttribute('data-playing');
    }
    function paintScrub(pct) {
      /* Paint the PLAYED part gold (range inputs don't fill themselves). */
      scrub.style.background = 'linear-gradient(to right, #FBD484 ' + pct + '%, rgba(255,255,255,0.25) ' + pct + '%)';
    }
    function syncTime() {
      var cur = video.currentTime || 0;
      var dur = video.duration || 0;
      timeLabel.textContent = fmt(cur) + ' / ' + fmt(dur);
      var pct = dur > 0 ? (cur / dur) * 100 : 0;
      scrub.value = pct;
      paintScrub(pct);
    }
    overlayEl.onclick = function () {
      if (video.paused) { video.play(); } else { video.pause(); }
    };
    video.onplay = function () { setPlaying(true); };
    video.onpause = function () { setPlaying(false); };
    video.onended = function () { setPlaying(false); };
    video.ontimeupdate = syncTime;
    video.onloadedmetadata = syncTime;
    scrub.oninput = function () {
      var dur = video.duration || 0;
      if (dur > 0) video.currentTime = (scrub.value / 100) * dur;
      paintScrub(scrub.value);
    };
    setPlaying(false);
    syncTime();
  }

  /* ---------- Pause / Resume / Finish (STOP RECORDING button on the layer) ----------
   * Tapping STOP RECORDING pauses BOTH the recording and the song, then opens a
   * modal: she can Resume (both restart together) or Finish & send (-> preview).
   * Built for the "kid walks in" interruption — never break her moment. */
  function pauseRecording() {
    if (state.engine && state.recordingActive) {
      try { state.engine.pause(); } catch (e) {}
    }
    if (songAudio && !songAudio.paused) songAudio.pause();
    /* Freeze the REC timer: stop the tick and remember when we paused. */
    if (state.elapsedTimerId) { clearInterval(state.elapsedTimerId); state.elapsedTimerId = null; }
    state.pausedAt = Date.now();
    showRecPauseModal(true);
    gtm('ugc_recorder_paused', {
      at_sec: state.startedAt ? Math.round((Date.now() - state.startedAt - state.pausedAccumMs) / 1000) : 0,
    });
  }
  function resumeRecording() {
    /* BUG5: during the salvage/finalize window the take is no longer live — a tap on
       Resume must NOT restart the REC timer or re-blast the song behind the
       interrupted view (it also leaked a 500ms interval). */
    if (state.finalizing || state.salvaging || !state.recordingActive) return;
    showRecPauseModal(false);
    /* Add the paused gap to the accumulator so the REC timer + recorded
       duration stay in sync (the engine's pause excludes paused time). */
    if (state.pausedAt) { state.pausedAccumMs += (Date.now() - state.pausedAt); state.pausedAt = null; }
    if (state.engine && state.recordingActive) {
      try { state.engine.resume(); } catch (e) {}
    }
    if (!state.elapsedTimerId) state.elapsedTimerId = setInterval(updateElapsed, 500);
    /* Only restart the song if it had actually started (don't auto-launch it
       when she paused during the intro/speak phase). */
    if (state.songStarted && songAudio) { var p = songAudio.play(); if (p && p.catch) p.catch(function () {}); }
    gtm('ugc_recorder_resumed');
  }
  /* ---------- "Saving…" feedback during the WebCodecs finalize ----------
   * The WebCodecs stop() chain (flush encoder → harvest+mux full audio track →
   * finalize fragmented MP4 → read a multi-MB file back from OPFS) takes ~1-4s on
   * a multi-minute take. The base MediaRecorder onstop was sub-100ms (invisible),
   * so on iOS we must tell her it's saving — otherwise she stares at the frozen
   * recording layer (stale red REC pill) and thinks it hung. ZERO markup added:
   * we reuse the existing #asRecToast (persistent, no auto-hide) AND overwrite the
   * frozen elapsed pill text so it no longer reads as a counting/hung timer. */
  var savingIndicatorShown = false;
  function setSavingIndicator(show) {
    if (show) {
      savingIndicatorShown = true;
      document.body.classList.add('as-rec-saving'); /* harmless if unstyled; future CSS hook */
      recToast('Saving your video… 💛', 600000);    /* persistent until onRecordingFinished hides it */
      var els = document.querySelectorAll('[data-rec-elapsed]');
      for (var i = 0; i < els.length; i++) els[i].textContent = 'Saving…';
    } else {
      /* True no-op when we never showed it → the MediaRecorder/Android-desktop
         path (which never calls show) is untouched, incl. the 8-min-limit toast. */
      if (!savingIndicatorShown) return;
      savingIndicatorShown = false;
      document.body.classList.remove('as-rec-saving');
      var el = document.getElementById('asRecToast');
      if (el) el.removeAttribute('data-show');
      if (recToastTimer) { clearTimeout(recToastTimer); recToastTimer = null; }
    }
  }
  function finishRecording() {
    showRecPauseModal(false);
    gtm('ugc_recorder_finish');
    finalizeStop();
  }
  function finalizeStop() {
    /* Engine stop() is async (WebCodecs finalizes the MP4 from OPFS). Drive it
       and route the resolved Blob through the shared finish handler. Used for
       BOTH the clean finish and salvage, exactly like the base used one
       mediaRecorder.stop() for both. */
    if (!state.recordingActive || !state.engine) return;
    if (state.elapsedTimerId) { clearInterval(state.elapsedTimerId); state.elapsedTimerId = null; }
    if (songAudio && !songAudio.paused) songAudio.pause();
    var wasSalvaged = state.salvaging;
    state.recordingActive = false; /* double-finish guard (a second finalizeStop no-ops) */
    /* Mark the finalize window so a racing onInterrupt does NOT hardClose/delete
       the OPFS scratch mid-drain (which would destroy the take + re-open the
       overlay with an empty blob). Cleared in onRecordingFinished. */
    state.finalizing = true;
    var eng = state.engine;
    /* WebCodecs finalize is slow + the camera light is killed inside stop(): show
       the "Saving…" feedback now so the frozen recording layer doesn't read as a
       hang. (MediaRecorder onstop is ~instant → no indicator needed; skipping it
       keeps the Android/desktop path visually identical to the base.) */
    if (eng.kind === 'webcodecs') setSavingIndicator(true); /* BUG5: on salvage too (cleared in onRecordingFinished) */
    var stopP;
    try { stopP = eng.stop(); } catch (e) { stopP = Promise.reject(e); }
    stopP.then(function (blob) {
      onRecordingFinished(blob, wasSalvaged);
    }).catch(function () {
      /* finalize failed entirely — still transition with whatever we have so she
         is never stuck on the recording layer. */
      var fallback = new Blob([], { type: 'video/mp4' });
      onRecordingFinished(fallback, wasSalvaged);
    });
  }

  /* ---------- Restore full-quality song playback after a mic session ----------
   * Opening the mic via getUserMedia switches the browser/OS audio OUTPUT into
   * "communication" mode (earpiece routing, AEC, reduced sample-rate) — the song
   * then plays back distant + choppy. Releasing the mic (track.stop) does NOT
   * restore the media route on an <audio> element that's already loaded; only a
   * page reload does (it rebuilds the element from scratch). So we do that
   * rebuild ourselves: reload #audio (preserving position) to force the browser
   * back onto the normal media route. NO Web Audio is involved here or anywhere
   * in the player — this is purely an output-route reset. Idempotent + cheap
   * (same already-cached MP3), safe to call from every mic-release path. */
  function restoreSongAudioRoute() {
    if (!songAudio || !songAudio.src) return;
    try {
      var t = songAudio.currentTime || 0;
      songAudio.load(); /* re-selects the render device now that the mic is gone */
      if (t > 0) {
        songAudio.addEventListener('loadedmetadata', function once() {
          songAudio.removeEventListener('loadedmetadata', once);
          try { songAudio.currentTime = t; } catch (e) {}
        });
      }
    } catch (e) {}
  }

  /* Release the camera/mic without tearing down the overlay (recording is done,
     we still need the overlay for the send flow). */
  function stopCameraTracks() {
    releaseWakeLock();
    teardownCanvas();
    /* Release the active engine's OWN devices (WebCodecs encoder + audio
       recorder + off-DOM source video, or MediaRecorder's mic recorder) here on
       the clean-finish path, alongside the camera — WITHOUT deleting the OPFS
       scratch (the read-back Blob is already in RAM; the scratch is deleted on
       discard / send / abandon via discard()). */
    if (state.engine && state.engine.hardRelease) {
      try { state.engine.hardRelease(); } catch (e) {}
    }
    var mirrors = document.querySelectorAll('[data-recorder-mirror]');
    for (var i = 0; i < mirrors.length; i++) {
      try { mirrors[i].pause(); mirrors[i].srcObject = null; mirrors[i].removeAttribute('src'); mirrors[i].load(); } catch (e) {}
    }
    if (state.mediaStream) {
      try { state.mediaStream.getTracks().forEach(function (t) { try { t.stop(); } catch (e) {} }); } catch (e) {}
      state.mediaStream = null;
    }
    restoreSongAudioRoute(); /* mic gone → rebuild #audio so playback isn't left in earpiece/AEC mode */
  }

  function discardRecording() {
    gtm('ugc_recorder_discarded');
    /* She chose to keep it to herself → remove the on-device copy too (the
       IndexedDB pending AND the WebCodecs OPFS scratch). */
    if (state.engine && state.engine.discard) { try { state.engine.discard(); } catch (e) {} }
    if (typeof window.astrosongUGCClearPending === 'function') window.astrosongUGCClearPending();
    hardCloseAndReleaseStream();
  }

  /* ---------- Interrupted-take recovery (returned after a lock / app switch) ----------
   * The take had to end (the OS releases the camera in the background). Instead
   * of silently dropping her on the Send screen, the 'interrupted' view lets her
   * choose. The partial stays in IndexedDB either way (card recovery). */
  function refilmAfterInterrupt() {
    gtm('ugc_recorder_refilm');
    clearPartialMarker(); /* BUG1: redoing the take → the orphan partial is moot */
    /* She's redoing it → drop the salvaged partial (IndexedDB + OPFS scratch) and
       start a clean take. */
    if (state.engine && state.engine.discard) { try { state.engine.discard(); } catch (e) {} }
    state.engine = null;
    if (typeof window.astrosongUGCClearPending === 'function') window.astrosongUGCClearPending();
    if (state.recordedUrl) { try { URL.revokeObjectURL(state.recordedUrl); } catch (e) {} state.recordedUrl = null; }
    state.recordedBlob = null;
    state.recordedChunks = [];
    state.open = false;          /* let open() proceed past its guard */
    open();                      /* re-acquires camera (no re-prompt) → Get ready */
  }
  function usePartialAfterInterrupt() {
    gtm('ugc_recorder_use_partial');
    setView('preview');          /* the preview video is already wired in onRecordingFinished */
  }

  /* ---------- Send flow (note → upload → success), all on the recorder ---------- */
  function buildRecordingFile() {
    var cleanType = (state.recordedBlob && state.recordedBlob.type ? state.recordedBlob.type : 'video/mp4').split(';')[0];
    var ext = cleanType === 'video/mp4' ? 'mp4' : cleanType === 'video/webm' ? 'webm' : 'mp4';
    return new File([state.recordedBlob], 'reaction-' + Date.now() + '.' + ext, { type: cleanType });
  }
  function setSendProgress(p) {
    var pct = Math.round((p || 0) * 100);
    var fill = $('[data-recorder-progress-fill]'); if (fill) fill.style.width = pct + '%';
    var el = $('[data-recorder-progress-pct]'); if (el) el.textContent = pct;
  }
  function sendRecording() {
    if (!state.recordedBlob) return;
    var pv = $('[data-recorder-preview-video]'); if (pv) { try { pv.pause(); } catch (e) {} }
    var file = buildRecordingFile();
    gtm('ugc_recorder_sent', { file_size_mb: Math.round(state.recordedBlob.size / 1048576) });
    /* She consented to send → the on-device scratch is no longer needed; delete
       the WebCodecs OPFS file (the Blob/File is in RAM). The card clears the
       IndexedDB pending on success. */
    if (state.engine && state.engine.discard) { try { state.engine.discard(); } catch (e) {} }
    /* Hand off to the CARD's upload UI so the sending / success / error screens
       are the SAME design as the other-device upload (one design, kept in sync —
       it IS the card). Close the recorder so just the card shows; the card runs
       the upload and persists the promo code on the page. The card clears the
       on-device pending recording on success. */
    hardCloseAndReleaseStream();
    if (typeof window.astrosongUGCStartUploadFromFile === 'function') {
      window.astrosongUGCStartUploadFromFile(file, { songStartMs: state.songStartMs });
    } else {
      alert('Upload unavailable — please refresh and try again.');
    }
  }

  /* Re-open the recorder on the PREVIEW step with a recording saved on the device
     (called by the card's "See your reaction video" after she left + came back).
     No camera is acquired — she just reviews → send / keep private / re-record. */
  window.astrosongUGCReviewBlob = function (blob, viewName) {
    if (!blob) return;
    state.recordedBlob = blob;
    if (state.recordedUrl) { try { URL.revokeObjectURL(state.recordedUrl); } catch (e) {} }
    state.recordedUrl = URL.createObjectURL(blob);
    state.open = false; /* no camera live → lifecycle handlers must not tear this down */
    overlay.setAttribute('data-open', 'true');
    document.body.classList.add('as-recorder-active');
    var preview = $('[data-recorder-preview-video]');
    if (preview) { preview.src = state.recordedUrl; preview.load(); wirePreviewControls(preview); }
    /* An interruption recovery (refresh / close mid-recording) lands on the SAME
       "You stepped away" screen as a soft interruption (see what I filmed / refilm);
       a deliberate card-button review (default) lands on the plain "Your reaction". */
    setView(viewName === 'interrupted' ? 'interrupted' : 'preview');
    gtm('ugc_recorder_review_saved', { as: (viewName === 'interrupted' ? 'interrupted' : 'preview') });
  };

  /* Copy the promo code from the success view */
  function copyPromoFromRecorder() {
    var btn = $('[data-recorder-promo-copy]');
    var hint = $('[data-recorder-promo-hint]');
    if (!btn) return;
    var code = btn.dataset.code || '';
    function done() {
      if (hint) { hint.textContent = 'Copied! ✓'; setTimeout(function () { hint.textContent = 'Tap to copy'; }, 1500); }
    }
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code).then(done).catch(done);
    } else { done(); }
    gtm('ugc_recorder_promo_copied');
  }

  /* ---------- THE PRIVACY-CRITICAL CLEANUP ----------
   * Called from every exit path. Idempotent (safe to call multiple times).
   * Releases the camera + mic IMMEDIATELY by:
   *   1. Releasing + DELETING the active engine (WebCodecs: encoder/audio/source
   *      video released AND the OPFS scratch MP4 deleted via discard())
   *   2. Detaching srcObject from all <video> mirrors (releases the browser's
   *      internal ref so the camera light can go off)
   *   3. Calling .stop() on every track in the MediaStream
   *   4. Loading() empty media so the <video> truly releases
   *   5. Nulling all our references so GC can collect
   *   6. Tearing down recording-mode UI + restoring the reveal page */
  function hardCloseAndReleaseStream() {
    releaseWakeLock();
    clearPartialMarker(); /* BUG1: teardown (send / discard / exit) → orphan partial no longer needed */
    /* Release the active engine on every exit path. discard() ALSO deletes the
       WebCodecs OPFS scratch (privacy-critical); MediaRecorder's discard just
       stops its recorder. Keeping state.mediaRecorder=null preserves the legacy
       null-out the rest of the code expects. */
    if (state.engine) {
      try { state.engine.discard ? state.engine.discard() : state.engine.hardRelease(); } catch (e) {}
      state.engine = null;
    }
    state.mediaRecorder = null;
    state.recordingActive = false;
    /* Tear down the 9:16 canvas capture (rAF loop + canvas stream + src video) */
    teardownCanvas();
    /* Detach all video mirrors (overlay framing mirror + layer PiP) from the
       stream FIRST so the browser releases its internal references */
    var mirrors = document.querySelectorAll('[data-recorder-mirror]');
    for (var i = 0; i < mirrors.length; i++) {
      try {
        mirrors[i].pause();
        mirrors[i].srcObject = null;
        mirrors[i].removeAttribute('src');
        mirrors[i].load();
      } catch (e) {}
    }
    /* Now stop all tracks — the camera light should go off here */
    if (state.mediaStream) {
      try {
        var tracks = state.mediaStream.getTracks();
        for (var j = 0; j < tracks.length; j++) {
          try { tracks[j].stop(); } catch (e) {}
        }
      } catch (e) {}
      state.mediaStream = null;
    }
    /* Timers + blob URL cleanup */
    if (state.elapsedTimerId) { clearInterval(state.elapsedTimerId); state.elapsedTimerId = null; }
    if (state.recordedUrl) { try { URL.revokeObjectURL(state.recordedUrl); } catch (e) {} state.recordedUrl = null; }
    /* Pause the song if we started it */
    try { if (songAudio && !songAudio.paused) songAudio.pause(); } catch (e) {}
    /* Mic is released above → rebuild #audio so it returns to the full-quality
       media route (covers exit-before-recording / discard / send / error paths). */
    restoreSongAudioRoute();
    /* Pause the preview video too so it never plays on in the background. */
    try { var pvc = $('[data-recorder-preview-video]'); if (pvc) pvc.pause(); } catch (e) {}
    /* Reset state + tear down all recorder UI */
    state.open = false;
    state.view = null;
    state.recordedChunks = [];
    state.recordedBlob = null;
    state.songStarted = false;
    state.startedAt = null;
    state.mimeType = null;
    state.pausedAccumMs = 0;
    state.pausedAt = null;
    state.warned = false;
    state.autoStopped = false;
    state.salvaging = false;
    overlay.removeAttribute('data-open');
    document.body.classList.remove('as-recorder-active');
    document.body.classList.remove('as-recording'); /* un-hide cards, hide layer */
    document.body.classList.remove('as-rec-speak');
    showRecPauseModal(false);
    setView(null);
    /* Reset the popup back to step 1 so a re-show (forcepopup / next visit)
       starts clean at the offer, not the permission step. */
    resetPopupToStep1();
  }

  /* Wraps hardCloseAndReleaseStream with the view-aware confirm step */
  function handleCloseRequest() {
    if (state.view === 'exit_confirm') {
      /* tapping X on the retention modal = leave */
      exitLeave();
      return;
    }
    if (state.view === 'preview') {
      /* Exit guard: gentle carrot, not a bare "discard?" */
      setView('keep_confirm');
      gtm('ugc_recorder_keep_prompted');
      return;
    }
    if (state.view === 'pre_record') {
      /* Gentle retention nudge before they bail */
      state.viewBeforeExit = state.view;
      setView('exit_confirm');
      gtm('ugc_recorder_exit_prompted', { from: state.viewBeforeExit });
      return;
    }
    /* countdown / permission_denied — close freely */
    hardCloseAndReleaseStream();
  }

  function exitStay() {
    gtm('ugc_recorder_exit_stayed');
    setView(state.viewBeforeExit || 'pre_record');
  }
  function exitLeave() {
    gtm('ugc_recorder_exit_left', { from: state.viewBeforeExit || '' });
    hardCloseAndReleaseStream();
    /* "Yes, just play my AstroSong without recording" → actually play it. */
    if (typeof window.astrosongMarkPopupResolved === 'function') window.astrosongMarkPopupResolved();
    if (typeof window.astrosongPlaySong === 'function') window.astrosongPlaySong();
  }

  /* Kill the camera light IMMEDIATELY without discarding the recording. Used on
     interruption while the recorder is still finalizing (the engine's stop()
     drains already-buffered data). Called AFTER engine.stop() is initiated so the
     buffered data is safe. Leaves state.mediaStream for onRecordingFinished's
     stopCameraTracks to null + detach the mirrors.
     ENGINE-AWARE: for WebCodecs we stop ONLY the camera (video) track here — the
     MIC must stay live until the engine's stop() chain harvests the audio-only
     MediaRecorder buffer (a synchronously-killed mic on iOS yields a SILENT
     salvage). The engine stops the mic itself after harvestAndMuxAudio. For
     MediaRecorder ONE recorder owns A+V and mr.stop() already buffered everything,
     so stopping all tracks immediately is safe + matches the base. */
  function releaseCameraTracksNow() {
    releaseWakeLock();
    if (!state.mediaStream) return;
    var webcodecs = !!(state.engine && state.engine.kind === 'webcodecs');
    try {
      var tracks = state.mediaStream.getTracks();
      for (var i = 0; i < tracks.length; i++) {
        var t = tracks[i];
        if (webcodecs && t.kind === 'audio') continue; /* keep mic live for the audio harvest */
        try { t.stop(); } catch (e) {}
      }
    } catch (e) {}
    /* The visible self-view also goes dark now that the camera is gone. */
    detachMirrorsFromStream();
  }

  /* Interrupted mid-recording (incoming call, app switch, screen lock, tab
     hidden). PRIVACY: release the camera/mic now. But NEVER lose her video —
     let the recorder finalize the FULL blob (onRecordingFinished persists it to
     IndexedDB for card recovery + shows the preview when she returns) instead of
     the destructive hardClose, which nukes the chunks before it can run.
     For WebCodecs the partial is a VALID fragmented MP4 (fastStart:'fragmented'),
     finalized from OPFS inside the engine's stop(). */
  function salvageAndRelease(reason) {
    if (state.salvaging) return;
    state.salvaging = true;
    /* BUG5: the pause modal (Resume/Finish) must not stay tappable over the salvage
       finalize — drop it the instant salvage starts. */
    showRecPauseModal(false);
    /* BUG7: if we were PAUSED when the interruption hit, fold the open pause window
       into pausedAccumMs BEFORE the gtm below so at_sec (and the later duration_sec)
       don't over-count by the in-progress gap. Idempotent (nulls pausedAt). */
    if (state.pausedAt) { state.pausedAccumMs += (Date.now() - state.pausedAt); state.pausedAt = null; }
    gtm('ugc_recorder_interrupted', {
      reason: reason || 'unknown',
      at_sec: state.startedAt ? Math.round((Date.now() - state.startedAt - state.pausedAccumMs) / 1000) : 0,
    });
    if (state.elapsedTimerId) { clearInterval(state.elapsedTimerId); state.elapsedTimerId = null; }
    try { if (songAudio && !songAudio.paused) songAudio.pause(); } catch (e) {}
    if (state.recordingActive && state.engine) {
      finalizeStop();                  /* → engine.stop() → onRecordingFinished (salvaged) */
    }
    releaseCameraTracksNow();            /* light off now; buffered data untouched */
  }

  /* ---------- Privacy safety nets — page lifecycle ----------
   * Lucas's #1 concern: a customer leaves the page (back home, switches apps,
   * incoming call, closes tab) and the camera/mic stays active. NEVER.
   * Single entry point so visibilitychange / pagehide / beforeunload can't race
   * each other (on reload, hidden fires THEN pagehide — the salvaging guard
   * stops the second one from hard-closing and nuking the salvaged chunks). */
  function onInterrupt(reason) {
    if (state.salvaging) return; /* salvage already in flight — don't double-handle */
    if (state.finalizing) {
      /* A clean-finish engine.stop() is draining (WebCodecs: SECONDS). Do NOT
         hardClose — that would discard()→delete the OPFS scratch mid-finalize and
         hand onRecordingFinished an EMPTY blob, then re-open the overlay. The take
         is already buffered; just release the camera light now WITHOUT touching
         the engine/scratch (the engine stops the mic itself after it harvests the
         audio). The pending stop() will resolve into the normal preview. */
      gtm('ugc_recorder_auto_released', { reason: (reason || 'unknown') + '_finalizing' });
      releaseCameraTracksNow();
      return;
    }
    if (state.recordingActive) {
      salvageAndRelease(reason);          /* mid-recording → SAVE, don't destroy */
    } else if (state.open) {
      gtm('ugc_recorder_auto_released', { reason: reason || 'unknown' });
      hardCloseAndReleaseStream();        /* nothing recorded yet → safe to hard close */
    }
    /* else: send/preview phase (state.open=false, recorder inactive) → leave it
       alone so backgrounding during the upload doesn't tear it down. */
  }

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') onInterrupt('visibility_hidden');
  });
  /* pagehide / beforeunload = a REAL page-leave (reload / back / close). Mark it so the
     salvage saves SILENTLY (no flashing interruption screen on the dying page) and
     defers to the clean next-load recovery. pagehide persisted=true = bfcache (the page
     will return, like an app-switch) → NOT a leave; pageshow clears the flag on restore. */
  window.addEventListener('pagehide', function (e) { if (!e || e.persisted !== true) unloading = true; onInterrupt('pagehide'); });
  window.addEventListener('beforeunload', function () { unloading = true; onInterrupt('beforeunload'); });
  window.addEventListener('pageshow', function () { unloading = false; });

  /* BUG1: on load, if a marker says a webcodecs take died in-flight (so no IDB blob
     exists), read the orphaned OPFS partial back and offer it for review. VIDEO-ONLY
     (the audio-only MediaRecorder buffer died with the page). Fully guarded — a clean
     no-op where OPFS is absent / no marker / the file is gone or too small. Self-
     contained: routes through this file's own astrosongUGCReviewBlob (no cross-IIFE
     timing race), and never collides with the card's IDB recovery (marker is cleared
     the moment an IDB blob exists). */
  function tryRecoverOrphanPartial() {
    try {
      if (!IS_IOS) return;                       /* OPFS partials are webcodecs/iOS only */
      var marked = false;
      try { marked = !!localStorage.getItem(partialMarkerKey()); } catch (e) {}
      if (!marked) return;
      if (!(navigator.storage && navigator.storage.getDirectory)) { clearPartialMarker(); return; }
      navigator.storage.getDirectory()
        .then(function (dir) { return dir.getFileHandle(WC_OPFS_FILENAME, { create: false }); })
        .then(function (fh) { return fh.getFile(); })
        .then(function (file) {
          if (file && file.size > 262144) {        /* >256KB ≈ a few seconds worth keeping */
            return file.arrayBuffer().then(function (ab) {
              clearPartialMarker();                /* one-shot */
              var blob = new Blob([ab], { type: 'video/mp4' });
              /* Blob is in RAM now → delete the OPFS scratch. After a load-time
                 recovery there is no engine to discard() it, so it would otherwise
                 linger until the next take truncates it. Best-effort. */
              try { navigator.storage.getDirectory().then(function (dd) { return dd.removeEntry(WC_OPFS_FILENAME); }).catch(function () {}); } catch (e) {}
              gtm('ugc_recorder_recovered_partial', { size_mb: Math.round(blob.size / 1048576) });
              if (typeof window.astrosongUGCReviewBlob === 'function') {
                window.astrosongUGCReviewBlob(blob, 'interrupted');
              }
            });
          }
          clearPartialMarker();                    /* nothing usable → clear, no recovery */
        })
        .catch(function () { clearPartialMarker(); }); /* file gone / unreadable → clear */
    } catch (e) {}
  }

  /* Unified on-load recovery: PREFER the reliable IndexedDB save (written by
     onRecordingFinished when the page lived long enough — it has audio) and auto-open
     the preview (send / refilm) — INDEPENDENT of the card's promo-code-gated success
     view, so a recovered reaction surfaces clearly even on a page that already carries
     a reward code. Falls back to the best-effort OPFS orphan ONLY when there is no IDB
     blob (true instant-kill). NEVER resumes recording (impossible after a page death). */
  function tryRecoverOnLoad() {
    try {
      if (typeof window.astrosongUGCGetPending === 'function') {
        window.astrosongUGCGetPending().then(function (rec) {
          if (rec && rec.blob && rec.blob.size > 0) {
            clearPartialMarker();
            gtm('ugc_recorder_recovered', { source: 'idb', size_mb: Math.round(rec.blob.size / 1048576) });
            if (typeof window.astrosongUGCReviewBlob === 'function') {
              window.astrosongUGCReviewBlob(rec.blob, 'interrupted'); /* "You stepped away" — it WAS an interruption */
            }
            return;
          }
          tryRecoverOrphanPartial();
        }).catch(function () { tryRecoverOrphanPartial(); });
      } else {
        tryRecoverOrphanPartial();
      }
    } catch (e) { try { tryRecoverOrphanPartial(); } catch (e2) {} }
  }

  function wire() {
    var bind = function (sel, evt, fn) { var el = $(sel); if (el) el.addEventListener(evt, fn); };
    var bindL = function (sel, evt, fn) { var el = $L(sel); if (el) el.addEventListener(evt, fn); };

    /* Overlay controls (pre_record / exit / preview) */
    bind('[data-recorder-close]', 'click', handleCloseRequest);
    bind('[data-recorder-exit-stay]', 'click', exitStay);
    bind('[data-recorder-exit-leave]', 'click', exitLeave);
    bind('[data-recorder-start]', 'click', startCountdown);
    bind('[data-recorder-send]', 'click', sendRecording);
    bind('[data-recorder-retry-send]', 'click', sendRecording);
    bind('[data-recorder-promo-copy]', 'click', copyPromoFromRecorder);
    bind('[data-recorder-done]', 'click', hardCloseAndReleaseStream);
    /* Send-screen exit guard (keep-it) */
    bind('[data-recorder-keepit]', 'click', function () { setView('keep_confirm'); gtm('ugc_recorder_keep_prompted'); });
    /* Re-record from the preview → CONFIRM first. The Re-record button sits right
       under Send; a finger-slip aiming for Send would otherwise wipe a good take
       with no undo. Pause the preview, then ask. */
    bind('[data-recorder-rerecord]', 'click', function () {
      var pv = $('[data-recorder-preview-video]'); if (pv) { try { pv.pause(); } catch (e) {} }
      setView('rerecord_confirm');
      gtm('ugc_recorder_rerecord_prompted');
    });
    /* Confirm-modal actions: cancel → back to the saved preview; confirm → wipe + refilm. */
    bind('[data-recorder-rerecord-cancel]', 'click', function () { setView('preview'); gtm('ugc_recorder_rerecord_cancelled'); });
    bind('[data-recorder-rerecord-confirm]', 'click', refilmAfterInterrupt);
    bind('[data-recorder-keep-back]', 'click', function () { setView('preview'); gtm('ugc_recorder_keep_back'); });
    bind('[data-recorder-keep-leave]', 'click', function () { gtm('ugc_recorder_keep_left'); discardRecording(); });

    /* Interrupted view (returned after a mid-recording lock / app switch) */
    bind('[data-recorder-refilm]', 'click', refilmAfterInterrupt);
    bind('[data-recorder-use-partial]', 'click', usePartialAfterInterrupt);

    /* Recording-layer controls (on the real reveal page). STOP RECORDING pauses
       both song + recording and opens the pause modal; Resume restarts both,
       Finish ends the take and goes to preview. */
    bindL('[data-rec-launch]', 'click', launchSong);
    bindL('[data-rec-stop]', 'click', pauseRecording);
    bindL('[data-rec-resume]', 'click', resumeRecording);
    bindL('[data-rec-finish]', 'click', finishRecording);

    /* Step 1 gold CTA → ask how she's filming (1 phone vs 2 devices) */
    var recordCta = document.querySelector('[data-fp-record-cta]');
    if (recordCta) recordCta.addEventListener('click', function () {
      gtm('ugc_recorder_clicked_from_popup');
      showPopupChoice();
    });
    /* Device choice: this phone → permission step; other device → prep + return */
    var choiceThis = document.querySelector('[data-fp-choice-this]');
    if (choiceThis) choiceThis.addEventListener('click', chooseThisPhone);
    var choiceOther = document.querySelector('[data-fp-choice-other]');
    if (choiceOther) choiceOther.addEventListener('click', chooseOtherDevice);
    var prep2Done = document.querySelector('[data-fp-prep2-done]');
    if (prep2Done) prep2Done.addEventListener('click', popupTwoDeviceDone);
    /* Step 2 gold CTA (this phone) → open recorder (requests camera/mic) */
    var allowCta = document.querySelector('[data-fp-allow-cta]');
    if (allowCta) allowCta.addEventListener('click', function () {
      gtm('ugc_recorder_allow_clicked');
      open();
    });
    /* "No thanks" → retention nudge (instead of dismissing). */
    var noThanksBtn = document.getElementById('startAfterPopup');
    if (noThanksBtn) noThanksBtn.addEventListener('click', showPopupRetain);
    /* Retention buttons */
    var retainStay = document.querySelector('[data-fp-retain-stay]');
    if (retainStay) retainStay.addEventListener('click', popupRetainStay);
    var retainLeave = document.querySelector('[data-fp-retain-leave]');
    if (retainLeave) retainLeave.addEventListener('click', popupRetainLeave);

    /* Back links — let her change her popup choice at any step */
    var backBtns = document.querySelectorAll('[data-fp-back]');
    for (var bi = 0; bi < backBtns.length; bi++) {
      (function (b) {
        b.addEventListener('click', function () {
          var target = b.getAttribute('data-fp-back') || '1';
          showPopupStep(target);
          gtm('ugc_recorder_popup_back', { to: target });
        });
      })(backBtns[bi]);
    }

    /* BUG1: surface a recovered reaction on load (IDB preferred, OPFS fallback). */
    tryRecoverOnLoad();
  }

  window.astrosongUGCOpenRecorder = open;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();
